import { comparer, makeAutoObservable, observable, reaction, runInAction } from "mobx";
import type { ColumnFiltersState, RowSelectionState, SortingState } from "@tanstack/react-table";
import type { RootStore } from "./root.store";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import {
  isColumnFiltersState,
  isSortingStateOver,
  readTablePref,
  writeTablePref,
  type TablePrefsKey,
} from "@/components/data-table/table-prefs-storage";
import { tablePrefsEnabledNow } from "@/components/data-table/table-prefs-gate";
import { type BuilderError, parseSaveErrors } from "@/lib/intake/parse-save-errors";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import { normalizeRichText } from "@/lib/rich-text";
import type { RequestViewFilterState } from "@/helpers/request-view.helper";
import type {
  TIntakeChoiceOption,
  TIntakeColumnsElement,
  TIntakeDefinition,
  TIntakeElement,
  TIntakeFieldGroupElement,
  TIntakeForm,
  TIntakeLocale,
  TIntakeLocalizedText,
  TIntakePage,
  SubmissionGroupBy,
  TIntakeSubmission,
  TIntakeSubmissionGroupStat,
  TIntakeSubmissionsArchivedView,
  TIntakeSubmissionsListQuery,
  TIntakeSystemFieldElement,
  TIntakeSystemFieldKind,
  TIntakeTreeElement,
  TWorkflowCurrentStage,
  TWorkflowDecisionResult,
  TWorkflowReturnResult,
  TStatusReasonSelection,
  TSingletonElementKind,
} from "@/types/intake";
import {
  DEFAULT_SYSTEM_FIELD_KINDS,
  OPTIONAL_SYSTEM_FIELD_KINDS,
  isRemovableSystemField,
} from "@/types/intake";
import type { TWorkspaceCustomField } from "@/types/custom-field";
import type { TRequestImportRequest, TRequestImportResult } from "@/types/intake-import";
import type { TPriorityTier } from "@/types/request-prioritization";
import {
  collectSubtreeIds,
  type ContainerPath,
  findElementById,
  getChildrenAtPath,
  iterTree,
  pathEquals,
  pathTouches,
  subtreeContainsSingleton,
} from "@/lib/intake/tree";
import { validateDraft } from "@/lib/intake/validate-draft";
import { isPermissionError } from "@/lib/is-permission-error";

/** Where a request was opened, for the ``request_viewed`` analytics event
 *  (NG-2057): the surface, and on the detail page which tab. Only the read that
 *  opens a request passes one; refreshes don't, so they count no view. */
type TRequestViewSource = { source: string; tab?: string };

/** Per-status pagination slice for the board view (one per status column). */
export interface TBoardColumnState {
  submissions: TIntakeSubmission[];
  cursor: string | null;
  hasMore: boolean;
  total: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
}

/** Default per-column page size for board infinite scroll. */
export const BOARD_COLUMN_PAGE_SIZE = 15;

function emptyBoardColumn(): TBoardColumnState {
  return {
    submissions: [],
    cursor: null,
    hasMore: false,
    total: 0,
    isLoading: false,
    isLoadingMore: false,
    error: null,
  };
}

/** A stored form as the builder's autosave body would send it, for diffing a
 *  save against it: the same defaults and draft overlay `hydrateFromForm` uses,
 *  and the same `description_html` normalization the save applies. */
function savedFormBody(form: TIntakeForm) {
  return {
    definition: injectMissingDefaultSystemFields(clone(form.definition)),
    name: form.name,
    description_html: normalizeRichText(form.description_html),
    teams: form.teams ?? [],
    request_type: form.request_type ?? "",
    default_request_category_value_id: form.default_request_category_value_id ?? null,
    default_request_owner_ids: form.default_request_owner_ids ?? [],
    managing_team_id: form.managing_team_id ?? form.managing_team?.id ?? null,
  };
}

function clone<T>(value: T): T {
  // Definitions are plain JSON, but values arriving from MobX observers can
  // be Proxy-wrapped, which `structuredClone` rejects. The JSON round-trip is
  // intentional: it strips any wrapper before the deep copy.
  return JSON.parse(JSON.stringify(value)) as T;
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function makeNewPage(): TIntakePage {
  return {
    id: newId(),
    name: { en: "New page", fr: "New page" },
    internal: false,
    visible_if: null,
    elements: [],
  };
}

type NewElementKind = Exclude<TIntakeElement["kind"], "system_field">;

/** Palette kinds that produce layout-only elements (not value-bearing). */
export type LayoutPaletteKind = "info_text" | "rich_text" | "divider" | "field_group" | "columns";

/**
 * Maps workspace bank field kinds to their intake element kinds.
 *
 * `satisfies` rather than a `Record<…, NewElementKind>` annotation on purpose:
 * the annotation widened every value to the full element-kind union, which hid
 * the fact that `makeNewElementFromBankField` handled only some of them and
 * downgraded the rest to `text` (NG-444). Inferring literals keeps the produced
 * union exact, so that switch's exhaustiveness check is worth something — while
 * `satisfies` still proves every bank kind is mapped to a real element kind.
 */
export const BANK_TO_INTAKE_KIND = {
  text: "text",
  paragraph: "textarea",
  number: "number",
  boolean: "checkbox",
  date: "datetime",
  select: "select",
  multiselect: "checkboxes",
  email: "email",
  phone: "phone",
  currency: "currency",
  user_list: "user_list",
} as const satisfies Record<TWorkspaceCustomField["kind"], NewElementKind>;

/**
 * A fresh choice option. The stored ``value`` is a generated, opaque, stable id
 * (same approach as element ``name``): it satisfies the backend's non-empty +
 * unique-per-element contract, never changes when the label is edited (so
 * existing submissions and ``visible_if`` conditions keep resolving), and is
 * mapped back to the label through the survey ``choices`` wherever an answer is
 * rendered. The label is seeded with a human default ("Option 1", "Option 2", …)
 * keyed to the option's position, editable from the Data tab (#2950) — before
 * this a blank label fell back to the opaque id, so a freshly added choice field
 * showed a UUID as its first option.
 */
function makeOption(index = 0): TIntakeChoiceOption {
  const label = `Option ${index + 1}`;
  return { value: newId(), label: { en: label, fr: label } };
}

function makeNewElement(kind: NewElementKind): TIntakeElement {
  const baseNamed = {
    id: newId(),
    name: newId(),
    label: { en: "New field", fr: "New field" } as TIntakeLocalizedText,
    internal: false,
    required: false,
    visible_if: null,
  };
  switch (kind) {
    case "text":
    case "textarea":
    case "email":
    case "url":
    case "phone":
    case "number":
    case "currency":
    case "datetime":
    case "file":
    case "checkbox":
    // Bank-driven member multiselect; options resolve at render time, so a
    // bare named element is all a (non-palette) new instance needs.
    case "user_list":
      return { ...baseNamed, kind };
    case "radio":
    case "checkboxes":
    case "select":
      return { ...baseNamed, kind, options: [makeOption()] };
    case "program":
      // Fixed display name — the program element is a singleton system-style field,
      // seeded (not "New field") so it reads correctly the moment it's added.
      return {
        ...baseNamed,
        kind: "program",
        label: { en: "Programs", fr: "Programmes" },
      };
    case "goal":
      // Seeded like program. ``multiple`` is set explicitly rather than left
      // undefined so the default is a stated choice (multi, matching program)
      // that the properties toggle then reflects, not an accident of falsiness.
      return {
        ...baseNamed,
        kind: "goal",
        label: { en: "Goals", fr: "Objectifs" },
        multiple: true,
      };
    case "request_category":
      // Fixed display name — the request-category element is a singleton
      // system-style field, seeded (not "New field") like ``program``.
      return {
        ...baseNamed,
        kind: "request_category",
        label: { en: "Request Category", fr: "Catégorie de demande" },
      };
    case "expression":
      return {
        ...baseNamed,
        kind: "expression",
        label: { en: "Calculated value", fr: "Calculated value" },
        // Calculated values are staff-facing by default (consistent with the
        // request-owner system field); the author flips Internal off to share
        // the result with requesters. The separate per-expression ``visible``
        // toggle was consolidated away 2026-06-10 — the flag remains honored
        // by the renderer for forms that already set it.
        internal: true,
        // Aggregate (sum/min/max/avg) is what authors reach for — product
        // decision 2026-06-10 — so new calculation fields start there instead
        // of a bare literal.
        expression: { type: "aggregate", op: "sum", args: [{ type: "literal", value: 0 }] },
      };
  }
}

function makeNewElementFromBankField(field: TWorkspaceCustomField): TIntakeElement {
  const intakeKind = BANK_TO_INTAKE_KIND[field.kind];
  const baseNamed = {
    id: newId(),
    name: newId(),
    label: { en: field.name, fr: field.name } as TIntakeLocalizedText,
    internal: false,
    required: false,
    visible_if: null,
    bank_field_id: field.id,
  };
  switch (intakeKind) {
    // Every scalar kind a bank field can map to needs a bare named element —
    // list them ALL. Omitting one used to fall through to the `default:` arm
    // below and silently produce a single-line `text` element, so a Global
    // paragraph/boolean/email/phone/currency field rendered as a text input
    // while its palette tile still showed the right icon (NG-444).
    case "text":
    case "textarea":
    case "number":
    case "checkbox":
    case "email":
    case "phone":
    case "datetime":
      return { ...baseNamed, kind: intakeKind };
    case "currency":
      // Snapshot the bank field's currency code the same way the choice kinds
      // below snapshot `display`, so the amount renders in the right currency.
      return {
        ...baseNamed,
        kind: "currency" as const,
        ...(field.config.currency ? { currency: field.config.currency } : {}),
      };
    case "select":
    case "checkboxes": {
      const options = (field.config.options ?? []).map((opt) => ({
        value: opt.value,
        label: { en: opt.label, fr: opt.label },
      }));
      if (intakeKind === "checkboxes") {
        // Carry the multi-select's display preference so the form renders a
        // dropdown (tagbox) vs checkboxes — snapshotted at add-time like options.
        return {
          ...baseNamed,
          kind: "checkboxes" as const,
          options,
          ...(field.config.display === "dropdown" ? { display: "dropdown" as const } : {}),
        };
      }
      // Carry the single-select's display preference so the form renders a
      // radio group vs dropdown — snapshotted at add-time like options. The
      // live bank display still wins at render time (PRD §3.1); this is the
      // fallback for a detached field.
      return {
        ...baseNamed,
        kind: "select" as const,
        options,
        ...(field.config.display === "radio" ? { display: "radio" as const } : {}),
      };
    }
    case "user_list":
      // Options are live workspace members — resolved at render time, never stored.
      return { ...baseNamed, kind: intakeKind };
    default: {
      // Exhaustiveness guard. `BANK_TO_INTAKE_KIND` covering every BANK kind is
      // not enough — this switch has to cover every kind that map can PRODUCE,
      // and it silently didn't (NG-444). Typing the fallthrough as `never` makes
      // `tsc` fail here instead, so adding a bank kind can't quietly downgrade
      // a field to text again. Keep the runtime fallback for a malformed field
      // arriving from the API, which the compiler can't see.
      const unhandled: never = intakeKind;
      console.warn("Unhandled bank field kind, falling back to text", {
        bankFieldId: field.id,
        bankFieldKind: field.kind,
        intakeKind: unhandled,
      });
      return { ...baseNamed, kind: "text" as const };
    }
  }
}

function isSystemFieldElement(el: TIntakeTreeElement): boolean {
  return el.kind === "system_field";
}

// Locked system fields are the auto-injected page-1 defaults — they cannot be
// deleted/renamed/reordered off the form. Everything in
// REMOVABLE_SYSTEM_FIELD_KINDS is the opposite: an author can take it off.
// request_owner has been removable since #2948 / #3089 (deleting it lets the
// form-level default owner fallback take over) and stayed removable when its ADD
// affordance was dropped; NG-690's launch date is removable and addable.
//
// Read through the shared set rather than naming a code here: this guard is what
// `removeElementAtPath` consults, so a removable field missing from the set
// cannot be deleted AT ALL, and the untick in the picker fails silently.
function isLockedSystemField(el: TIntakeTreeElement): boolean {
  return el.kind === "system_field" && !isRemovableSystemField(el);
}

function makeLayoutElement(kind: LayoutPaletteKind): TIntakeTreeElement {
  switch (kind) {
    case "info_text":
      return {
        id: newId(),
        kind: "info_text",
        content: { en: "Informational text", fr: "Texte informatif" },
      };
    case "rich_text":
      return {
        id: newId(),
        kind: "rich_text",
        content: { en: "<p></p>", fr: "<p></p>" },
      };
    case "divider":
      return { id: newId(), kind: "divider" };
    case "field_group": {
      const group: TIntakeFieldGroupElement = {
        id: newId(),
        kind: "field_group",
        label: { en: "Group", fr: "Groupe" },
        collapsed: false,
        children: [],
      };
      return group;
    }
    case "columns": {
      const cols: TIntakeColumnsElement = {
        id: newId(),
        kind: "columns",
        columns: [
          { id: newId(), children: [] },
          { id: newId(), children: [] },
        ],
      };
      return cols;
    }
  }
}

export function isLayoutPaletteKind(kind: string): kind is LayoutPaletteKind {
  return (
    kind === "info_text" ||
    kind === "rich_text" ||
    kind === "divider" ||
    kind === "field_group" ||
    kind === "columns"
  );
}

/**
 * Re-assigns ``id`` (and field ``name`` for leaves) recursively on a duplicated
 * subtree. The root keeps ``rootId`` so callers can re-select the duplicate.
 */
function regenerateSubtreeIds(element: TIntakeTreeElement, rootId: string): void {
  element.id = rootId;
  if (element.kind === "field_group") {
    for (const child of element.children) regenerateSubtreeIds(child, newId());
  } else if (element.kind === "columns") {
    for (const column of element.columns) {
      column.id = newId();
      for (const child of column.children) regenerateSubtreeIds(child, newId());
    }
  } else if (element.kind !== "system_field" && "name" in element) {
    element.name = newId();
  }
}

function annotateDuplicateLabels(element: TIntakeTreeElement): void {
  if (element.kind === "system_field") return;
  if ("label" in element && element.label) {
    element.label = {
      en: `${element.label.en} (copy)`,
      fr: `${element.label.fr} (copy)`,
    };
  }
}

const SYSTEM_FIELD_LABELS: Record<TIntakeSystemFieldKind, TIntakeLocalizedText> = {
  request_name: { en: "Request name", fr: "Nom de la demande" },
  requester: { en: "Requester", fr: "Demandeur" },
  request_owner: { en: "Request owner", fr: "Responsable de la demande" },
  request_reviewer: { en: "Request reviewer", fr: "Réviseur de la demande" },
  target_launch_date: { en: "Target launch date", fr: "Date de lancement cible" },
};

function makeSystemFieldElement(kind: TIntakeSystemFieldKind): TIntakeSystemFieldElement {
  // request_owner defaults to internal (hidden from the requester) and optional
  // (#2694): when no owner is submitted the request auto-assigns the workflow's
  // default request owner (form-level default_request_owner_ids). Mirrors the
  // backend new-form default in `_empty_definition`.
  const isOwner = kind === "request_owner";
  return {
    id: kind,
    kind: "system_field",
    system_field: kind,
    label: { ...SYSTEM_FIELD_LABELS[kind] },
    internal: isOwner,
    // OPTIONAL_SYSTEM_FIELD_KINDS, not `!isOwner`: the launch date is optional
    // too, and the backend enforces the split whatever this claims — so hardcoding
    // it here only ever published a definition asserting the wrong thing.
    required: !OPTIONAL_SYSTEM_FIELD_KINDS.has(kind),
    visible_if: null,
  };
}

/**
 * Inject any DEFAULT_SYSTEM_FIELD_KINDS missing from the definition at the top
 * of the first page, in canonical order. Idempotent.
 *
 * Mirrors the backend's ``inject_missing_default_system_fields`` so the builder
 * stays consistent even if a definition loads from a stale source (legacy form
 * created before the data migration ran, dev re-seed, etc.). Defense-in-depth.
 */
function injectMissingDefaultSystemFields(definition: TIntakeDefinition): TIntakeDefinition {
  if (!definition.pages?.length) return definition;
  const present = new Set<string>();
  for (const page of definition.pages) {
    for (const { element } of iterTree(page)) {
      if (element.kind === "system_field") present.add(element.system_field);
    }
  }
  const firstPage = definition.pages[0];
  if (!firstPage) return definition;
  let insertAt = 0;
  for (const kind of DEFAULT_SYSTEM_FIELD_KINDS) {
    if (present.has(kind)) continue;
    firstPage.elements.splice(insertAt, 0, makeSystemFieldElement(kind));
    insertAt += 1;
    present.add(kind);
  }
  return definition;
}

function pageContainsSystemField(page: TIntakePage): boolean {
  for (const { element } of iterTree(page)) {
    if (isSystemFieldElement(element)) return true;
  }
  return false;
}

// System fields are locked against delete/required/rename, but their position
// is fully reorderable — authors can drop new fields anywhere and rearrange
// system fields freely. Just clamp into [0, length].
function clampInsertIndex(page: TIntakePage, atIndex: number): number {
  return Math.max(0, Math.min(atIndex, page.elements.length));
}

type ChoiceElement = Extract<TIntakeElement, { options: TIntakeChoiceOption[] }>;

function isChoiceElement(el: TIntakeTreeElement | undefined): el is ChoiceElement {
  return el?.kind === "radio" || el?.kind === "checkboxes" || el?.kind === "select";
}

interface TIntakeFormTopLevel {
  name: string;
  /** Authored rich-text HTML. The plain `description` is derived server-side,
   *  so the builder only ever holds and sends this half (NG-352). */
  descriptionHtml: string;
  is_active: boolean;
  teams: string[];
  request_type: string;
  default_request_category_value_id: string | null;
  default_request_owner_ids: string[];
  /** Managing Team id (#3144) — the OWNER axis, single-select, so a scalar and
   *  not a list like ``teams`` (the Submitting audience). ``null`` is
   *  workspace-managed. */
  managingTeamId: string | null;
}

function emptyTopLevel(): TIntakeFormTopLevel {
  return {
    name: "",
    descriptionHtml: "",
    is_active: false,
    teams: [],
    request_type: "",
    default_request_category_value_id: null,
    default_request_owner_ids: [],
    managingTeamId: null,
  };
}

/** Must match the `tableId` the list surface passes to `<DataTable>` and to
 *  `useDataTableColumnPrefs` (`intake-submissions-list.tsx`) — the persisted
 *  sort/filters share that table's key namespace. */
const SUBMISSIONS_TABLE_ID = "intake-submissions";

/** Only the columns the BE allow-list can sort. Others are not sortable. */
const SUBMISSIONS_COLUMN_TO_ORDER_BY: Record<string, string> = {
  status: "status",
  requestedBy: "requested_by",
  formUsed: "form",
  created: "submitted_at",
  lastUpdated: "updated_at",
  // Urgency sort: most overdue first → ascending remaining → no-SLA last (BE).
  sla: "sla",
  // Priority sort: highest score first (desc) → nulls last (BE).
  priority: "priority",
  // Request Category sort: the BE orders on the category value's own `order`
  // (the sequence the settings editor defines), not its label, and puts
  // uncategorised requests last in both directions (NG-492).
  requestCategory: "category",
};

/**
 * Validates a stored submissions sort against the ids it may legitimately
 * hold — the map's own keys, so the two cannot drift (NG-629).
 *
 * Derived rather than written out because that map is already this surface's
 * definition of "sortable": `intake-submissions-columns.tsx` marks exactly
 * these columns `enableSorting: true`, and an id outside it resolves to the
 * empty token, i.e. no server sort at all. Before this, a stored id left behind
 * by a renamed column passed shape validation, suppressed the default sort, and
 * sat in `submissionsSorting` where no header could clear it.
 *
 * Built once at module scope rather than per hydration, so re-entering the list
 * does not rebuild the Set behind it.
 */
const isSubmissionsSorting = isSortingStateOver(Object.keys(SUBMISSIONS_COLUMN_TO_ORDER_BY));

/**
 * Keep a RESOLVED ``can_edit`` when a row is replaced by one that never evaluated it
 * (#3144 D7).
 *
 * ``can_edit`` is tri-state and only the DETAIL read resolves it: the list answers
 * ``null`` ("never evaluated") by contract, and so does every write echo (PATCH,
 * publish, discard, the active toggle). So a list page — or a save's own response —
 * landing on top of a detail-fetched row would silently un-resolve the answer and
 * drop the surface back into its unresolved gate mid-edit.
 *
 * This merge is for the write/list paths ONLY. It must never be applied to a DETAIL
 * response: that read is the authority, and pinning the first answer it ever gave is
 * how an owner reassignment goes stale (see ``setActiveForm``).
 */
function withResolvedCanEdit(next: TIntakeForm, previous: TIntakeForm | undefined): TIntakeForm {
  if (typeof next.can_edit === "boolean") return next;
  if (typeof previous?.can_edit !== "boolean") return next;
  return { ...next, can_edit: previous.can_edit };
}

/**
 * Append-to-the-end insertion index. ``insertElementAtPath`` clamps into the
 * target container, so an out-of-range index lands at its tail — the same idiom
 * the drag handlers use for a container/root tail drop.
 */
const TAIL = Number.MAX_SAFE_INTEGER;

/** One row picked in the builder's "Add Global Field" modal (NG-689). The two
 *  singletons have no bank record, so they cannot be addressed by field id. */
export type TGlobalFieldPick =
  | { type: "program" }
  | { type: "goal" }
  | { type: "request_category" }
  // Parameterised rather than a `{type:"target_launch_date"}` arm, so a future
  // addable system field needs a picker row and nothing here. Note this is NOT
  // a route back for `request_owner`: a July 2026 product decision removed its
  // add affordance deliberately (it stays removable, never addable).
  | { type: "system_field"; systemField: TIntakeSystemFieldKind }
  | { type: "bank"; bankFieldId: string };

export class IntakeStore {
  rootStore: RootStore;
  forms: TIntakeForm[] = [];
  activeFormId: string | null = null;
  /** The form being filled in the requester flow (`/intake/requests/:formId`).
   *  Set by IntakeRequesterFlow so the primary breadcrumb can label the crumb
   *  with the form name — the requester flow owns its forms in local state, not
   *  `forms`, so this is the breadcrumb's only handle on the active form. */
  activeRequestForm: { id: string; name: string } | null = null;
  activePageId: string | null = null;
  selectedElementId: string | null = null;
  /**
   * Container ids the author has folded away **on the builder canvas** (NG-1087).
   *
   * INVARIANT: builder view state — never serialised into the definition, never
   * read by the requester renderer. The requester-facing default is a different
   * field, `TIntakeFieldGroupElement.collapsed`, which documents why the two must
   * stay apart.
   *
   * Nothing resets it, deliberately. Element ids are unique across forms, so a
   * fold cannot leak between them, and the set therefore needs no hook in
   * `hydrateFromForm` — which is what lets a fold survive the autosave that
   * replaces the draft ~1s after every edit, and lets it survive navigating away
   * and back. It holds one id per container the author folded this session, so
   * ids of deleted elements are harmless: nothing looks them up.
   */
  builderCollapsedContainerIds = observable.set<string>();
  /** Transient "reveal this element" signal (#2518): bumping the nonce tells the
   *  element's field row to scroll into view + flash a destructive ring. Nonce so
   *  re-requesting the same id re-fires the reveal. */
  elementFlash: { elementId: string | null; nonce: number } = { elementId: null, nonce: 0 };
  /**
   * Where the global-field picker puts what it adds (NG-853).
   *
   * Held here rather than in the palette's local state because every container
   * on the canvas can open the one mounted modal, and each has to name its own
   * destination: the palette's button means the page root (``[]``), a column or
   * field-group button means that container's path. Do not render a second modal
   * per container — they would fight over this one target.
   *
   * INVARIANT: this OUTLIVES the picker closing, which is why it is separate
   * from ``globalFieldPickerOpen`` rather than doubling as it. The create-a-
   * field sub-flow closes the picker *before* opening the create modal
   * (`global-field-picker-dialog.tsx`), so a field created from inside a column
   * is added after the close — collapse the two and that add silently lands at
   * the page root, which is the very bug NG-853 fixes.
   */
  globalFieldPickerTarget: { pageIndex: number; path: ContainerPath } = { pageIndex: 0, path: [] };
  /** Whether the global-field picker is showing. Paired with, but deliberately
   *  not folded into, ``globalFieldPickerTarget`` — see the invariant there. */
  globalFieldPickerOpen = false;
  /** Which locale the builder's properties pane edits — the EN/FR inputs
   *  collapsed into a single field driven by this switch. */
  builderLocale: TIntakeLocale = "en";
  /**
   * Choice-option labels only: show EN and FR side by side instead of just
   * ``builderLocale`` (NG-444). Off by default — two half-width inputs per row
   * truncate a real-length label, which is the readability problem this fixes.
   * On the store rather than in the OptionsEditor so it survives selecting a
   * different element; as component state a translator re-checked it per field.
   */
  showBothOptionLocales = false;
  draft: TIntakeDefinition | null = null;
  topLevel: TIntakeFormTopLevel = emptyTopLevel();
  isLoading = false;
  isSaving = false;
  isCreating = false;
  isPublishing = false;
  /** ISO timestamp of the last successful autosave; ``null`` if the form
   *  is fully published with no pending draft. Drives "Saved Xs ago". */
  draftUpdatedAt: string | null = null;
  /** ISO timestamp of the last successful Publish. */
  publishedAt: string | null = null;
  hasUnpublishedChanges = false;
  /** A detail read for the ACTIVE workflow is in flight, so its ``can_edit`` answer is
   *  not in yet (#3144 D7). The detail surface gates its write affordances on this
   *  rather than rendering them optimistically — see ``activeFormCanEdit``. */
  isResolvingCanEdit = false;
  /** The message from the last FAILED ``can_edit`` resolution for the active workflow,
   *  ``null`` when the answer resolved (or no read has run). A failed read means we do
   *  not know whether this caller may edit, so it un-resolves the answer instead of
   *  leaving the previous one armed — the surface stays read-only and the route toasts
   *  this. */
  canEditError: string | null = null;
  loadError: string | null = null;
  inlineErrors: BuilderError[] = [];
  bannerErrors: string[] = [];
  submissions: TIntakeSubmission[] = [];
  // Per-submission render definition served from the run's pinned snapshot via
  // the /api/v2/ route (#3160). Kept apart from the submission's
  // ``form_detail.definition`` (the legacy blob route, which serves an empty
  // definition when the snapshot fails the strict validity gate — the prod
  // crash source). Keyed by submission id; populated by fetchSubmissionDefinition.
  submissionDefinitions: Map<string, TIntakeDefinition> = new Map();
  activeSubmissionId: string | null = null;
  isLoadingSubmissions = false;
  isLoadingMoreSubmissions = false;
  submissionsError: string | null = null;
  /** The first-page load was refused with a 403 (NG-2032): a permanent denial,
   *  which the list shows as no access rather than a retryable error. */
  submissionsDenied = false;
  // Form Workflow runtime "awaiting my review" queue (epic #2166, BE #2170):
  // the in-flight requests paused on a Review that lists the current user as a
  // reviewer. Separate from ``submissions`` (the admin requests list) — a
  // reviewer need not be a workspace admin, so this feeds its own reviewer page.
  pendingReviews: TIntakeSubmission[] = [];
  isLoadingPendingReviews = false;
  /** False until the first fetch settles (NG-2128). The fetch runs in an effect,
   *  after the first paint, so ``isLoadingPendingReviews`` alone read as "loaded,
   *  nothing to review" for a frame and the queue showed "0 awaiting" first. */
  hasLoadedPendingReviews = false;
  pendingReviewsError: string | null = null;
  // Cursor-pagination state for the requests-list page. ``submissions`` is
  // the union of loaded pages so existing optimistic mutators (delete,
  // updateSubmissionStatus, patchSubmissionResponseData) keep operating on
  // the cached slice without caring about page boundaries.
  submissionsCursor: string | null = null;
  submissionsHasMore = false;
  submissionsTotalCount = 0;
  // Per-status pagination for the board view. Each status column paginates
  // independently (its own cursor/hasMore/total) by filtering the list query to
  // a single ``state`` id, so a column can lazy-load via infinite scroll without
  // pulling the whole flat list. Keyed by state id. Shares the list's
  // search/filter/archive state through ``buildSubmissionsListQuery``.
  boardColumns: Map<string, TBoardColumnState> = new Map();
  // Server-side filter state, one field per BE filter param on
  // ``IntakeSubmissionListEndpoint``. The list page's column-header filters
  // call the matching ``setSubmissions...`` setter, which assigns the value
  // and then re-fetches the first page so the cursor window matches the new
  // filter set.
  submissionsFormFilter: string[] = [];
  submissionsProgramFilter: string[] = [];
  submissionsSearch = "";
  submissionsStatusFilter: string[] = [];
  submissionsRequestedByFilter: string[] = [];
  submissionsOwnerFilter: string[] = [];
  submissionsTeamFilter: string[] = [];
  /** Multi-id managed Request Category filter (NG-421). Matches the request's
   *  ``category_value_id``, so a legacy request showing a request-type fallback
   *  label is not selected by the category of the same name. */
  submissionsCategoryFilter: string[] = [];
  /** Multi-state SLA adherence filter (within/paused/breached/met). */
  submissionsSlaFilter: string[] = [];
  /** Multi-tier priority filter (urgent/high/medium/low). */
  submissionsPriorityFilter: string[] = [];
  submissionsCreatedAfter = "";
  submissionsCreatedBefore = "";
  submissionsUpdatedAfter = "";
  submissionsUpdatedBefore = "";
  /** Active (default, hides archived) | archived (only archived) | all. */
  submissionsArchivedView: TIntakeSubmissionsArchivedView = "active";
  // DataTable bridge: TanStack-shaped sort/selection state. ``submissionsOrderBy``
  // is the resolved server token derived from ``submissionsSorting`` and sent on
  // the list query. ``submissionsColumnFilters`` (getter below) bridges the flat
  // filter fields above to the TanStack ColumnFiltersState the primitive consumes.
  submissionsSorting: SortingState = [];
  submissionsOrderBy = "";
  submissionsRowSelection: RowSelectionState = {};
  // Server-side group-by for the table view (#1470). When set to a dimension,
  // the list fetch sends ``group_by`` and the endpoint orders the page by that
  // field so each group's rows are contiguous; the client renders a group
  // header when the key changes. The board view groups by status inherently
  // and ignores this. ``submissionsGroups`` holds the server's complete
  // per-group counts (#1524) so headers show true totals, not the loaded window.
  submissionsGroupBy: SubmissionGroupBy = "none";
  submissionsGroups: TIntakeSubmissionGroupStat[] = [];
  // The workspace whose stored list prefs are currently loaded, and therefore
  // the only key `persistSubmissionsPref` may write.
  //
  // Deliberately NOT `rootStore.router.workspaceSlug`: `RouterStore.params` is
  // set from an effect, so during a workspace switch the router still holds the
  // OUTGOING slug while the list is already mounting for the incoming one —
  // which would file the incoming workspace's filters under the outgoing
  // workspace's key. The slug hydration was called with comes straight from the
  // route param the surface renders for, so it cannot lag.
  //
  // Not observable: bookkeeping about a read, nothing renders it.
  private submissionsPrefsSlug: string | null = null;
  // True only while `hydrateSubmissionsPrefs` is applying a value it just READ,
  // so the persistence reactions below don't write it straight back.
  private hydratingSubmissionsPrefs = false;
  /**
   * Tracked as a flag (set on every mutation, cleared on hydrate/save) instead of computed
   * via JSON.stringify(draft), which would re-serialize the whole definition on every
   * keystroke in the properties pane.
   */
  private _dirty = false;
  /** In-flight PATCH so ``flushPendingSave`` can await a debounce that already
   *  fired, instead of overlapping a second write. Not observable — a Promise
   *  is not a value the UI should react to. */
  private saveInFlight: Promise<void> | null = null;
  /** Bumped by every ``fetchSubmissions``. Two quick filter changes fire two
   *  page-1 requests with no ordering guarantee, so a response (or an in-flight
   *  load-more page) whose number is no longer current belongs to a superseded
   *  filter set and must write nothing (NG-1724). Not observable: nothing
   *  renders it. */
  private submissionsSeq = 0;
  /** The board's per-column counterpart of ``submissionsSeq``: bumped by every
   *  ``fetchBoardColumn`` for that status, since a filter change refetches each
   *  column's page 1 the same way (NG-1724). Not observable. */
  private boardColumnSeq = new Map<string, number>();
  /** Row writes a list page must not undo (NG-2463). ``fetchSubmissions``
   *  replaces the whole list with its page, and the server can have read that
   *  page before a row's write committed: a search typed just before an inline
   *  status change resolved after it and put the old status back on the row.
   *  ``writingSubmission`` ticks the clock when a row write starts and when it
   *  settles, and counts it in flight meanwhile; a page keeps the cached copy of
   *  any row whose write is in flight or settled after the page was requested.
   *  Not observable: nothing renders them. */
  private submissionWriteClock = 0;
  private submissionWrittenAt = new Map<string, number>();
  private submissionWritesInFlight = new Map<string, number>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        submissionsPrefsSlug: false,
        hydratingSubmissionsPrefs: false,
        submissionsSeq: false,
        boardColumnSeq: false,
        submissionWriteClock: false,
        submissionWrittenAt: false,
        submissionWritesInFlight: false,
        // NOT an action, deliberately — see `hydrateSubmissionsPrefs`. MobX
        // flushes pending reactions when the OUTERMOST action ends, so if this
        // method were an action its own `runInAction` would not flush them and
        // the write-back guard inside it would already have been cleared by the
        // time the persistence reactions ran.
        hydrateSubmissionsPrefs: false,
      } as never,
      { autoBind: true },
    );
    // Opting out of the action wrapping above also opts out of `autoBind`, and
    // store methods are routinely lifted off the instance in this codebase.
    // Bind it back so that stays safe.
    this.hydrateSubmissionsPrefs = this.hydrateSubmissionsPrefs.bind(this);
    this.watchSubmissionsListPrefs();
  }

  // ---- Persisted list preferences (NG-517) ----

  /**
   * The ONE place the submissions list sort + filters are persisted.
   *
   * Deliberately a reaction over the DERIVED state rather than a write in each
   * setter. Thirteen mutators write the state these two keys hold — the twelve
   * flat facet setters behind the board's Filter popover and
   * `setSubmissionsColumnFilters` — so wiring the write to one of them would
   * leave storage diverging from state: clearing a facet the facade never saw
   * would leave the stored entry behind and resurrect it on the next reload.
   * Reacting to the derived value covers every mutator, including the next one
   * someone adds.
   *
   * `submissionsColumnFilters` is the SAME value the surface hands to
   * `<DataTable>`, so what gets stored is the whole set the user sees.
   *
   * No disposer: `IntakeStore` lives for the lifetime of the app, so these
   * reactions are never torn down.
   */
  private watchSubmissionsListPrefs(): void {
    reaction(
      () => this.submissionsColumnFilters,
      (filters) => this.persistSubmissionsPref("columnFilters", filters),
      // Structural, so re-deriving an equal set is not a write — the getter
      // builds a fresh array on every evaluation.
      { equals: comparer.structural },
    );
    reaction(
      () => [...this.submissionsSorting],
      (sorting) => this.persistSubmissionsPref("sorting", sorting),
      { equals: comparer.structural },
    );
  }

  private submissionsPrefsKey(slug: string): TablePrefsKey {
    // `|| null` is a second line of defence — `persistSubmissionsPref` and
    // `hydrateSubmissionsPrefs` both bail on a falsy slug. An empty-string
    // scope collapses to the UNSCOPED key, which collides with the legacy
    // column-prefs namespace and is orphaned the moment a real slug arrives.
    return { tableId: SUBMISSIONS_TABLE_ID, scope: slug || null };
  }

  private persistSubmissionsPref(
    slice: "sorting" | "columnFilters",
    value: SortingState | ColumnFiltersState,
  ): void {
    // Behind `persisted-table-prefs` (NG-517) — this store reaches
    // `writeTablePref` directly, so the hook-side gate does not cover it.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    // Hydration applies a value it just READ; writing it back would be a
    // pointless write and would misreport who last wrote the key.
    if (this.hydratingSubmissionsPrefs) return;
    // No hydrated workspace means we do not know whose key this is. That is
    // only reachable before the list has mounted, where there is nothing worth
    // persisting anyway.
    if (!this.submissionsPrefsSlug) return;
    writeTablePref(this.submissionsPrefsKey(this.submissionsPrefsSlug), slice, value);
  }

  /**
   * Reset the submissions list's session state and restore this workspace's
   * stored sort + filters.
   *
   * Called as the FIRST statement of the list's mount / workspace-change effect,
   * which then triggers the initial `fetchSubmissions` — so that one request
   * already carries the restored sort and filters instead of an unfiltered
   * request being followed by a filtered one. (There is no `clientLoader` here:
   * the load is effect-driven, and that effect is not keyed on the filter
   * state, so hydrating at the top of it cannot re-fire it.)
   *
   * The reset is folded IN rather than left to the caller on purpose. It used
   * to be a bare `clearSubmissionsFilters()` in that effect; run before
   * hydration from outside the write-back guard, the cleared value would reach
   * the persistence reaction and wipe the very key hydration is about to read.
   *
   * Idempotent: it derives the whole persisted state from storage, so calling it
   * twice for the same slug lands on the same state — and re-entering a
   * workspace re-reads that workspace's key rather than carrying the previous
   * one's entity ids.
   */
  hydrateSubmissionsPrefs(slug: string): void {
    if (!slug) return;
    const key = this.submissionsPrefsKey(slug);
    // Behind `persisted-table-prefs` (NG-517). Gated at the READS rather than
    // with an early return, because this method also owns the session RESET
    // folded in above — bailing outright would leave one workspace's filters
    // applied after a switch to another, which is a bug the flag must not be
    // able to cause. Restoring nothing is exactly the pre-NG-517 behaviour: the
    // reset still runs, and the empty arrays below are what it used to apply.
    const restore = tablePrefsEnabledNow(this.rootStore.featureFlags);
    // `readTablePref` distinguishes absent from empty, so a user who cleared
    // the sort gets `[]` back rather than a default.
    const sorting = restore
      ? readTablePref<SortingState>(key, "sorting", [], isSubmissionsSorting)
      : [];
    const filters = restore
      ? readTablePref<ColumnFiltersState>(key, "columnFilters", [], isColumnFiltersState)
      : [];
    // The guard has to still be set when the persistence reactions run. MobX
    // flushes them as the OUTERMOST action ends — which is why this method is
    // excluded from `makeAutoObservable`'s action wrapping (see the
    // constructor): that makes the `runInAction` below the outermost action, so
    // the reactions fire inside it, while the guard is up. All observable writes
    // here must therefore stay inside that `runInAction`.
    this.hydratingSubmissionsPrefs = true;
    try {
      runInAction(() => {
        this.submissionsPrefsSlug = slug;
        this.clearSubmissionsFilters();
        this.applySubmissionsSorting(sorting);
        this.applySubmissionsColumnFilters(filters);
      });
    } finally {
      this.hydratingSubmissionsPrefs = false;
    }
  }

  setActiveRequestForm(form: { id: string; name: string } | null): void {
    this.activeRequestForm = form;
  }

  get form(): TIntakeForm | null {
    if (!this.activeFormId) return null;
    return this.forms.find((f) => f.id === this.activeFormId) ?? null;
  }

  /**
   * The authoring answer for the ACTIVE workflow, as the detail surface must read it
   * (#3144 D7). TRI-STATE and deliberately so:
   *
   * - ``true`` — the detail read said this caller may edit. Arm the write affordances.
   * - ``false`` — the detail read said view-only. Say so (banner) and withdraw them.
   * - ``null`` — **unresolved**: a list row we have not read the detail for yet, a read
   *   still in flight, or one that failed. NOT "editable": rendering the editor here is
   *   the fail-open this axis exists to close, so the surface stays read-only until an
   *   answer lands. ``isResolvingCanEdit`` separates "waiting" (skeleton) from "we
   *   asked and could not find out" (``canEditError``).
   *
   * A failed read un-resolves a previously-armed surface on purpose: editability turns
   * over with the workflow's owner, so the last answer we happen to hold is not
   * evidence the caller can still save.
   */
  get activeFormCanEdit(): boolean | null {
    if (this.canEditError) return null;
    const answer = this.form?.can_edit;
    return typeof answer === "boolean" ? answer : null;
  }

  get isDirty(): boolean {
    if (!this.form || !this.draft) return false;
    return this._dirty;
  }

  /**
   * Client-side preflight errors derived from the current draft. Computed
   * lazily in the property-pane render path (no store mutation), which keeps
   * the existing post-400 ``inlineErrors`` plumbing untouched — both sources
   * are merged via ``allInlineErrors`` so the inline-error chip renderer
   * doesn't have to care where an error came from.
   */
  get clientInlineErrors(): BuilderError[] {
    return validateDraft(this.draft);
  }

  /** Merged backend (post-400) + client-side preflight inline errors. */
  get allInlineErrors(): BuilderError[] {
    return [...this.clientInlineErrors, ...this.inlineErrors];
  }

  /**
   * True when the draft passes client-side preflight. Wired to the Save button's
   * ``disabled`` prop so authors can't fire a request that the backend will
   * reject for the well-known set of required-field omissions. Backend remains
   * the source of truth — this is a UX accelerator.
   */
  get isDraftValid(): boolean {
    if (!this.draft) return false;
    return this.clientInlineErrors.length === 0;
  }

  /** Why the Publish button should be disabled (or null when enabled).
   *  Mirrors the PRD gates so the FE tooltip + the BE 409/422 stay in
   *  sync: the form must be Active, name + default request category must be
   *  set, and the draft must be valid + actually contain pending changes.
   *
   *  The default Request Owner is deliberately NOT checked here (#3643). It is
   *  only required when the workflow can't assign an owner itself, and whether
   *  every route through the graph reaches a Change Owner node is a server-side
   *  question — re-deriving it client-side would duplicate a graph walk and go
   *  stale the moment the rule changes. A form that genuinely still needs a
   *  default gets the BE's 422, which the publish handler already surfaces by
   *  name. */
  get publishDisabledReason():
    | "no_changes"
    | "inactive"
    | "missing_required"
    | "invalid_draft"
    | null {
    if (!this.form) return "no_changes";
    if (!this.hasUnpublishedChanges) return "no_changes";
    if (!this.topLevel.is_active) return "inactive";
    if (!this.topLevel.name.trim() || !this.topLevel.default_request_category_value_id) {
      return "missing_required";
    }
    if (!this.isDraftValid) return "invalid_draft";
    return null;
  }

  get canPublish(): boolean {
    return this.publishDisabledReason === null;
  }

  /** Refresh only server-owned metadata, leaving the editable draft/topLevel
   *  and the dirty flag untouched. Used after an autosave that returned while
   *  the user was still editing, so the stale echo can't clobber newer edits. */
  private applyServerMeta(form: TIntakeForm): void {
    this.draftUpdatedAt = form.draft_updated_at ?? null;
    this.publishedAt = form.published_at ?? null;
    this.hasUnpublishedChanges = Boolean(form.has_unpublished_changes);
  }

  private hydrateFromForm(form: TIntakeForm): void {
    this.activeFormId = form.id;
    this.draft = injectMissingDefaultSystemFields(clone(form.definition));
    this.topLevel = {
      name: form.name,
      descriptionHtml: form.description_html,
      is_active: form.is_active,
      teams: form.teams ?? [],
      request_type: form.request_type ?? "",
      default_request_category_value_id: form.default_request_category_value_id ?? null,
      default_request_owner_ids: form.default_request_owner_ids ?? [],
      // ``managing_team_id`` already carries the draft overlay when the form was
      // fetched with ``include_draft``; ``managing_team`` is the same value with
      // the name attached, so it is the fallback for a response that only
      // serialized the object form.
      managingTeamId: form.managing_team_id ?? form.managing_team?.id ?? null,
    };
    // Author meta — undefined for non-author responses, so default to safe.
    this.draftUpdatedAt = form.draft_updated_at ?? null;
    this.publishedAt = form.published_at ?? null;
    this.hasUnpublishedChanges = Boolean(form.has_unpublished_changes);
    // Preserve activePageId across re-hydration (e.g. after save) when the page
    // still exists. Without this, saving while editing a non-first page would
    // snap the builder back to page 1. Falls back to the first page on initial
    // load or when the active page was removed server-side.
    const previousActivePageId = this.activePageId;
    const pageStillExists =
      previousActivePageId !== null &&
      form.definition.pages.some((p) => p.id === previousActivePageId);
    this.activePageId = pageStillExists
      ? previousActivePageId
      : (form.definition.pages[0]?.id ?? null);
    // Preserve selectedElementId for the same reason: with autosave firing
    // every ~1s after a drop, clearing the selection on every PATCH return
    // makes the right-hand config pane flicker and snap shut while the
    // user is still editing the new element. Keep the selection when the
    // element still lives somewhere in the new draft tree.
    const previousSelectedId = this.selectedElementId;
    const selectedStillExists =
      previousSelectedId !== null &&
      this.draft !== null &&
      this.draft.pages.some((p) => findElementById(p, previousSelectedId) !== null);
    this.selectedElementId = selectedStillExists ? previousSelectedId : null;
    this._dirty = false;
    this.clearErrors();
  }

  setActivePage(pageId: string): void {
    if (!this.draft) return;
    if (this.draft.pages.some((p) => p.id === pageId)) {
      this.activePageId = pageId;
      this.selectedElementId = null;
    }
  }

  setBuilderLocale(locale: TIntakeLocale): void {
    this.builderLocale = locale;
  }

  /** Is this container folded away on the builder canvas? */
  isBuilderContainerCollapsed(containerId: string): boolean {
    return this.builderCollapsedContainerIds.has(containerId);
  }

  /** Fold / unfold a container on the builder canvas. Writes nothing to the
   *  definition, so it can never disturb "Collapsed by default". */
  toggleBuilderContainerCollapsed(containerId: string): void {
    if (this.builderCollapsedContainerIds.has(containerId)) {
      this.builderCollapsedContainerIds.delete(containerId);
    } else {
      this.builderCollapsedContainerIds.add(containerId);
    }
  }

  setShowBothOptionLocales(showBoth: boolean): void {
    this.showBothOptionLocales = showBoth;
  }

  selectElement(id: string | null): void {
    if (id === null) {
      this.selectedElementId = null;
      return;
    }
    if (!this.draft) return;
    const activePage = this.draft.pages.find((p) => p.id === this.activePageId);
    if (!activePage) return;
    if (findElementById(activePage, id)) {
      this.selectedElementId = id;
    }
  }

  /** Select an element AND ask its field row to scroll into view + flash — used
   *  by the Edit Page modal's blank-option banner to reveal the offending field
   *  even when the field list is long. */
  revealElement(id: string): void {
    this.selectElement(id);
    this.elementFlash = { elementId: id, nonce: this.elementFlash.nonce + 1 };
  }

  get selectedElement(): TIntakeTreeElement | null {
    if (!this.draft || !this.selectedElementId) return null;
    const activePage = this.draft.pages.find((p) => p.id === this.activePageId);
    if (!activePage) return null;
    const found = findElementById(activePage, this.selectedElementId);
    return found?.element ?? null;
  }

  /**
   * Location (parent path + index) for the currently selected element, walking
   * the page tree. ``null`` when nothing is selected or the id has dropped off
   * the tree (e.g. parent container was deleted).
   */
  get selectedElementLocation(): { path: ContainerPath; index: number } | null {
    if (!this.draft || !this.selectedElementId) return null;
    const activePage = this.draft.pages.find((p) => p.id === this.activePageId);
    if (!activePage) return null;
    const found = findElementById(activePage, this.selectedElementId);
    return found?.location ?? null;
  }

  /**
   * @deprecated Top-level-index accessor retained for back-compat with the
   * flat properties-pane API. Tree-aware callers should use
   * ``selectedElementLocation`` instead.
   */
  get selectedElementIndex(): number | null {
    const loc = this.selectedElementLocation;
    if (!loc) return null;
    return loc.path.length === 0 ? loc.index : null;
  }

  async load(slug: string): Promise<void> {
    this.isLoading = true;
    this.loadError = null;
    try {
      // ``includeDraft: true`` is the builder-only overlay flag —
      // top-level fields reflect the draft layer for forms with pending
      // autosaved edits. Without it the builder would read back the live
      // (last-published) version and a freshly-autosaved field would
      // visually disappear until publish.
      const list = await this.rootStore.intakeService.listForms(slug, { includeDraft: true });
      runInAction(() => {
        // The list never evaluates ``can_edit``; a row we already resolved from the
        // detail read must keep its answer (see ``withResolvedCanEdit``).
        const previousById = new Map(this.forms.map((f) => [f.id, f]));
        const merged = list.map((f) => withResolvedCanEdit(f, previousById.get(f.id)));
        const previousActive = this.activeFormId;
        const refreshedActive = previousActive
          ? merged.find((f) => f.id === previousActive)
          : undefined;
        // A workflow the DETAIL read is holding can legitimately be missing from a list
        // read — a just-duplicated row against a replica that hasn't caught up is the
        // common one. Dropping it from the cache empties the `form` getter under the
        // detail page, which then sits on its skeleton forever, so carry it over
        // (NG-175).
        const strandedActive =
          previousActive && !refreshedActive ? previousById.get(previousActive) : undefined;
        this.forms = strandedActive ? [strandedActive, ...merged] : merged;

        if (previousActive) {
          // This is a LIST read; it does not get to decide which workflow is open.
          // Re-pointing `activeFormId` at `list[0]` because the active one wasn't in
          // the page is what stranded the detail route. Refresh the active workflow
          // when the list has it — unless the author is mid-edit, in which case a
          // late-landing list row would retype the field under them — and otherwise
          // leave the detail read's hydration alone.
          if (refreshedActive && !this._dirty) this.hydrateFromForm(refreshedActive);
        } else if (merged[0]) {
          // No workflow selected: the forms page opens the first one.
          this.hydrateFromForm(merged[0]);
        } else {
          this.activeFormId = null;
          this.draft = null;
          this.topLevel = emptyTopLevel();
        }
      });
    } catch (err) {
      console.warn("[intake] load forms failed", { slug }, err);
      runInAction(() => {
        this.loadError = (err as Error).message;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  selectForm(formId: string): void {
    const next = this.forms.find((f) => f.id === formId);
    if (!next) return;
    this.hydrateFromForm(next);
  }

  /** ``managingTeamId`` seeds the new workflow's owner (#3144). It is resolved
   *  by the caller (which knows *where* the create was started from) and only
   *  sent when it is known — never guessed, so a workflow whose owner is
   *  ambiguous is born workspace-managed and the author picks one. */
  async createForm(
    slug: string,
    name?: string,
    options: { managingTeamId?: string | null } = {},
    source: AnalyticsSource = "page",
  ): Promise<TIntakeForm | null> {
    this.isCreating = true;
    try {
      const created = await this.rootStore.intakeService.createForm(slug, {
        name: name?.trim() || "Untitled workflow",
        ...(options.managingTeamId ? { managing_team_id: options.managingTeamId } : {}),
      });
      runInAction(() => {
        this.forms = [created, ...this.forms];
        this.hydrateFromForm(created);
      });
      track("form_created", { form_id: created.id, source });
      return created;
    } catch (err) {
      // Shown to the author as a banner; logged so the failure has its context.
      console.error("intake: could not create a workflow", { slug, err });
      runInAction(() => {
        this.bannerErrors = [(err as Error).message];
      });
      return null;
    } finally {
      runInAction(() => {
        this.isCreating = false;
      });
    }
  }

  /** Bulk-import requests into a form from parsed CSV rows (NG-784). Returns the
   *  per-row report. The caller (the import wizard) owns retry/summary UX, so
   *  this only makes the call and records analytics. */
  async importForm(
    slug: string,
    formId: string,
    body: TRequestImportRequest,
  ): Promise<TRequestImportResult> {
    const report = await this.rootStore.intakeService.importForm(slug, formId, body);
    track("requests_bulk_imported", {
      created: report.created_count,
      errors: report.error_count,
      total: report.total,
    });
    // The request's own create event too, so request counts include imports
    // (NG-2057): one event for the whole batch, carrying how many it created.
    if (report.created_count > 0) {
      track("intake_request_submitted", {
        form_id: formId,
        count: report.created_count,
        source: "import",
      });
    }
    return report;
  }

  // Every topLevel setter hands back a NEW object (like ``draft``) rather than
  // mutating in place. The autosave effect (forms-edit.tsx) keys its debounce
  // timer on ``topLevel`` identity; an in-place mutation kept the reference
  // stable, so the effect never re-ran to reset the timer — collapsing the 1s
  // debounce into a 1s throttle that fired mid-keystroke and dropped letters.
  setFormName(name: string): void {
    this.topLevel = { ...this.topLevel, name };
    this._dirty = true;
    this.clearErrors();
  }

  setFormDescriptionHtml(descriptionHtml: string): void {
    this.topLevel = { ...this.topLevel, descriptionHtml };
    this._dirty = true;
    this.clearErrors();
  }

  setFormTeams(teamIds: string[]): void {
    this.topLevel = { ...this.topLevel, teams: [...teamIds] };
    this._dirty = true;
    this.clearErrors();
  }

  /** Managing Team — the ownership axis (#3144). ``null`` clears the pending
   *  draft change; it does not make a published workflow workspace-managed. */
  setFormManagingTeam(teamId: string | null): void {
    this.topLevel = { ...this.topLevel, managingTeamId: teamId };
    this._dirty = true;
    this.clearErrors();
  }

  setFormRequestType(requestType: string): void {
    this.topLevel = { ...this.topLevel, request_type: requestType };
    this._dirty = true;
    this.clearErrors();
  }

  setFormDefaultRequestCategory(valueId: string | null): void {
    this.topLevel = { ...this.topLevel, default_request_category_value_id: valueId };
    this._dirty = true;
    this.clearErrors();
  }

  setFormDefaultRequestOwners(ownerIds: string[]): void {
    this.topLevel = { ...this.topLevel, default_request_owner_ids: [...ownerIds] };
    this._dirty = true;
    this.clearErrors();
  }

  applyDefinitionPatch(updater: (draft: TIntakeDefinition) => void): void {
    if (!this.draft) return;
    const next = clone(this.draft);
    updater(next);
    this.draft = next;
    if (this.selectedElementId) {
      const activePage = next.pages.find((p) => p.id === this.activePageId);
      const stillThere = activePage ? findElementById(activePage, this.selectedElementId) : null;
      if (!stillThere) this.selectedElementId = null;
    }
    this._dirty = true;
    this.clearErrors();
  }

  clearErrors(): void {
    this.inlineErrors = [];
    this.bannerErrors = [];
  }

  discard(): void {
    if (!this.form) return;
    const previousActivePageId = this.activePageId;
    this.hydrateFromForm(this.form);
    if (previousActivePageId && this.draft?.pages.some((p) => p.id === previousActivePageId)) {
      this.activePageId = previousActivePageId;
    }
  }

  addPage(): void {
    const newPage = makeNewPage();
    this.applyDefinitionPatch((draft) => {
      draft.pages.push(newPage);
    });
    this.activePageId = newPage.id; // jump to the new tab so the user sees what they added
  }

  removePage(pageIndex: number): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page || pageContainsSystemField(page)) return;
    const wasActive = this.activePageId === page.id;
    this.applyDefinitionPatch((draft) => {
      draft.pages.splice(pageIndex, 1);
    });
    if (wasActive && this.draft) {
      // Fall back to the page now occupying the removed index, else the last page.
      const fallback = this.draft.pages[Math.min(pageIndex, this.draft.pages.length - 1)] ?? null;
      this.activePageId = fallback?.id ?? null;
    }
  }

  movePage(fromIndex: number, toIndex: number): void {
    if (!this.draft) return;
    if (fromIndex === toIndex) return;
    const source = this.draft.pages[fromIndex];
    if (!source) return;
    if (pageContainsSystemField(source) && toIndex !== 0) return;
    const target = this.draft.pages[toIndex] ?? this.draft.pages[this.draft.pages.length - 1];
    if (target && pageContainsSystemField(target) && fromIndex !== 0) return;
    this.applyDefinitionPatch((draft) => {
      const [moved] = draft.pages.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(toIndex, draft.pages.length));
      draft.pages.splice(insertAt, 0, moved);
    });
  }

  setPageName(pageIndex: number, locale: "en" | "fr", value: string): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (page) page.name[locale] = value;
    });
  }

  setPageInternal(pageIndex: number, value: boolean): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (page) page.internal = value;
    });
  }

  setPageVisibleIf(pageIndex: number, condition: TIntakePage["visible_if"]): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (page) page.visible_if = condition;
    });
  }

  /**
   * Insert ``element`` into the children array addressed by ``path`` at
   * ``atIndex``. When ``path`` is ``[]`` the insertion respects the
   * system-field clamp (system fields always stay at the top of page 1).
   */
  insertElementAtPath(
    pageIndex: number,
    path: ContainerPath,
    atIndex: number,
    element: TIntakeTreeElement,
  ): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const target = getChildrenAtPath(page, path);
    if (!target) return;
    const clamped =
      path.length === 0
        ? clampInsertIndex(page, atIndex)
        : Math.max(0, Math.min(atIndex, target.length));
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const list = getChildrenAtPath(draftPage, path);
      if (!list) return;
      list.splice(clamped, 0, element);
    });
    this.selectElement(element.id);
  }

  /** Remove the element at ``path[index]``. No-op for locked (default) system
   *  fields; the placeable request_owner (#2948) is removable (#3089). */
  removeElementAtPath(pageIndex: number, path: ContainerPath, index: number): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const list = getChildrenAtPath(page, path);
    const el = list?.[index];
    if (!el || isLockedSystemField(el)) return;
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const target = getChildrenAtPath(draftPage, path);
      if (!target) return;
      target.splice(index, 1);
    });
  }

  /**
   * Move an element between two paths within the same page. Cycles are
   * rejected (cannot drop a container into its own subtree).
   */
  moveElementBetweenPaths(
    pageIndex: number,
    fromPath: ContainerPath,
    fromIndex: number,
    toPath: ContainerPath,
    toIndex: number,
  ): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const fromList = getChildrenAtPath(page, fromPath);
    const source = fromList?.[fromIndex];
    if (!source) return;
    // System fields are locked against delete/required/rename, but their
    // position is reorderable like any other element.

    // Reparent cycle guard — refuse to drop a container into its own subtree.
    if (source.kind === "field_group" || source.kind === "columns") {
      const forbidden = collectSubtreeIds(source);
      if (pathTouches(toPath, forbidden)) return;
    }

    if (pathEquals(fromPath, toPath) && fromIndex === toIndex) return;

    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const draftFrom = getChildrenAtPath(draftPage, fromPath);
      if (!draftFrom) return;
      const [moved] = draftFrom.splice(fromIndex, 1);
      if (!moved) return;
      const draftTo = getChildrenAtPath(draftPage, toPath);
      if (!draftTo) {
        // Path disappeared mid-mutation (shouldn't happen, but restore).
        draftFrom.splice(fromIndex, 0, moved);
        return;
      }
      // ``toIndex`` is already a post-removal insertion index — the DnD layer's
      // ``computeDropIndex`` compensates for the dragged element shifting its
      // siblings down on a same-list move. Re-applying that ``fromIndex < toIndex``
      // decrement here double-counted it, so downward drags landed one slot short
      // (a one-row move became a no-op). Insert at ``toIndex`` directly, matching
      // ``movePage`` / ``moveOptionAtPath``.
      const clamped =
        toPath.length === 0
          ? clampInsertIndex(draftPage, toIndex)
          : Math.max(0, Math.min(toIndex, draftTo.length));
      draftTo.splice(clamped, 0, moved);
    });
  }

  addElement(pageIndex: number, kind: NewElementKind): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    this.insertElementAtPath(pageIndex, [], page.elements.length, makeNewElement(kind));
  }

  /** Add a layout element (info_text / divider / field_group / columns). */
  addLayoutElement(pageIndex: number, kind: LayoutPaletteKind): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    this.insertElementAtPath(pageIndex, [], page.elements.length, makeLayoutElement(kind));
  }

  /** Add a layout element at a specific path + index. */
  insertLayoutElement(
    pageIndex: number,
    path: ContainerPath,
    atIndex: number,
    kind: LayoutPaletteKind,
  ): void {
    this.insertElementAtPath(pageIndex, path, atIndex, makeLayoutElement(kind));
  }

  /** ``path`` is second and required, like the singleton adders below — an
   *  optional destination defaulting to the page root is precisely the
   *  assumption NG-853 had to undo. */
  addElementFromBankField(pageIndex: number, path: ContainerPath, bankFieldId: string): void {
    const field = this.rootStore.workspaceCustomFields.fields.find((f) => f.id === bankFieldId);
    if (!field) return;
    this.insertElementAtPath(pageIndex, path, TAIL, makeNewElementFromBankField(field));
  }

  /** True iff the draft already holds ``kind`` anywhere — any page, any nesting
   *  depth. Deliberately draft-wide, so the guard is unaffected by which
   *  container a new one was aimed at. */
  hasSingletonKind(kind: TSingletonElementKind): boolean {
    for (const page of this.draft?.pages ?? []) {
      for (const { element } of iterTree(page)) {
        if (element.kind === kind) return true;
      }
    }
    return false;
  }

  /** Whether any page already carries this system field. A placed system field is
   *  one-per-FORM, not one-per-page: its answer key is the field's code, so a
   *  second element anywhere would give one key two competing answers. The
   *  backend rejects that on field-name uniqueness; this is what stops the
   *  picker offering the add in the first place. */
  hasSystemFieldElement(kind: TIntakeSystemFieldKind): boolean {
    for (const page of this.draft?.pages ?? []) {
      for (const { element } of iterTree(page)) {
        if (element.kind === "system_field" && element.system_field === kind) return true;
      }
    }
    return false;
  }

  /** ``path`` is second and required, like the other add paths — an optional
   *  destination defaulting to the page root is the assumption NG-853 undid.
   *  A launch date picked from inside a column belongs in that column. */
  addSystemFieldElement(
    pageIndex: number,
    path: ContainerPath,
    kind: TIntakeSystemFieldKind,
  ): void {
    if (this.hasSystemFieldElement(kind)) return; // one per form
    // A MINTED id, overriding `makeSystemFieldElement`'s `id: kind`. That
    // code-as-id is the canonical legacy-`definition` shape and is right for the
    // injection path, but a placed element is persisted as a `PageElement` whose
    // primary key is a UUID — `definition_to_page_elements` passes the id through
    // verbatim, so `id: "target_launch_date"` makes the page save 400.
    //
    // Safe for the answer: a system field's `response_data` key is its CODE, not
    // its id (`fieldName` on the FE, `_field_name` on the BE), so the id is
    // identity-only here. The one-per-form rule is likewise enforced on the code
    // via definition-wide field-name uniqueness, not on the id.
    this.insertElementAtPath(pageIndex, path, TAIL, {
      ...makeSystemFieldElement(kind),
      id: newId(),
    });
  }

  /**
   * Append a singleton element to the container at ``path``, or no-op when the
   * draft already holds one.
   *
   * ``path`` is second and required — an optional destination defaulting to the
   * page root is precisely the assumption NG-853 had to undo.
   */
  private addSingletonElement(
    pageIndex: number,
    path: ContainerPath,
    kind: TSingletonElementKind,
  ): void {
    if (this.hasSingletonKind(kind)) return;
    this.insertElementAtPath(pageIndex, path, TAIL, makeNewElement(kind));
  }

  hasProgramElement(): boolean {
    return this.hasSingletonKind("program");
  }

  addProgramElement(pageIndex: number, path: ContainerPath): void {
    this.addSingletonElement(pageIndex, path, "program");
  }

  hasGoalElement(): boolean {
    return this.hasSingletonKind("goal");
  }

  addGoalElement(pageIndex: number, path: ContainerPath): void {
    this.addSingletonElement(pageIndex, path, "goal");
  }

  hasRequestCategoryElement(): boolean {
    return this.hasSingletonKind("request_category");
  }

  addRequestCategoryElement(pageIndex: number, path: ContainerPath): void {
    this.addSingletonElement(pageIndex, path, "request_category");
  }

  /**
   * Append every picked global field to the container addressed by ``path`` in
   * one action, and reveal the first one (it can land below the fold). Backs the
   * builder's "Add Global Field" modal, which is the only way to add a global
   * field now that the palette no longer lists them (NG-689).
   *
   * ``path`` is the container the author asked for: ``[]`` for the palette's
   * button (append to the page), or a column / field-group path when the modal
   * was opened from inside that container. Before NG-853 this only ever
   * appended to the page root, so a global field meant for a column landed
   * beneath it and had to be dragged in by hand — the drag path it replaced
   * had honoured the container.
   *
   * Singleton picks route through ``addSingletonElement`` and a placed system
   * field through ``addSystemFieldElement``, so their one-per-form guards still
   * hold — a modal left open while the draft changed underneath cannot
   * double-add.
   */
  addGlobalFieldsAtPath(pageIndex: number, path: ContainerPath, picks: TGlobalFieldPick[]): void {
    if (!this.draft) return;
    let firstAddedId: string | null = null;
    for (const pick of picks) {
      if (pick.type === "bank") this.addElementFromBankField(pageIndex, path, pick.bankFieldId);
      else if (pick.type === "system_field")
        this.addSystemFieldElement(pageIndex, path, pick.systemField);
      else this.addSingletonElement(pageIndex, path, pick.type);
      // Each successful insert selects the element it created; a guard that
      // refused the add leaves the previous selection in place, so only take
      // the id once and only when it actually moved.
      if (firstAddedId === null) firstAddedId = this.selectedElementId;
    }
    if (firstAddedId) this.revealElement(firstAddedId);
  }

  /**
   * Take every picked global field off the draft, wherever it sits — the
   * remove half of the builder's global-field modal (NG-689), which opens with
   * the attached fields ticked and treats unticking as removal.
   *
   * Scoped to the whole draft, not to one page: the modal lists what the form
   * holds, so a field placed on page 3 is untickable from page 1 and has to be
   * found across every page and every nested container. Each pick re-scans,
   * because removing one shifts the sibling indices of the next.
   *
   * Routed through ``removeElementAtPath`` so the locked-system-field guard and
   * the clone-and-replace patch invariant still hold. Like deleting from the
   * canvas, this does NOT rewrite a ``visible_if`` on another element that
   * referenced the removed field; the builder's error list is what surfaces the
   * dangling reference, and that behaviour is shared with the existing delete.
   */
  removeGlobalFieldsFromDraft(picks: TGlobalFieldPick[]): void {
    for (const pick of picks) {
      const found = this.locateGlobalField(pick);
      if (!found) continue;
      this.removeElementAtPath(found.pageIndex, found.path, found.index);
    }
  }

  /** Open the global-field picker aimed at one container. ``path`` is ``[]`` for
   *  the page root, which is what the palette's own button passes. Required, not
   *  defaulted: silently falling back to the root is the NG-853 bug. */
  openGlobalFieldPicker(pageIndex: number, path: ContainerPath): void {
    this.globalFieldPickerTarget = { pageIndex, path };
    this.globalFieldPickerOpen = true;
  }

  /** Closes the picker but KEEPS the target — see the invariant on it. */
  closeGlobalFieldPicker(): void {
    this.globalFieldPickerOpen = false;
  }

  /** The page index the builder is editing. Both a missing draft and a page id
   *  that no longer resolves fall back to the first page. */
  get activePageIndex(): number {
    const index = this.draft?.pages.findIndex((p) => p.id === this.activePageId);
    return index == null || index < 0 ? 0 : index;
  }

  /** Where a picked global field currently sits in the draft, or null. */
  private locateGlobalField(
    pick: TGlobalFieldPick,
  ): { pageIndex: number; path: ContainerPath; index: number } | null {
    const pages = this.draft?.pages ?? [];
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      const page = pages[pageIndex];
      if (!page) continue;
      for (const { element, location } of iterTree(page)) {
        // `element.kind === pick.type` works for the singleton kinds because
        // their pick type IS the kind. A system-field pick is the exception: its
        // kind is the literal "system_field" and the code lives on the element,
        // so comparing against `pick.type` would never match and the untick
        // would silently do nothing.
        const matches =
          pick.type === "bank"
            ? (element as { bank_field_id?: string }).bank_field_id === pick.bankFieldId
            : pick.type === "system_field"
              ? element.kind === "system_field" && element.system_field === pick.systemField
              : element.kind === pick.type;
        if (matches) return { pageIndex, path: location.path, index: location.index };
      }
    }
    return null;
  }

  insertElement(pageIndex: number, atIndex: number, kind: NewElementKind): void {
    this.insertElementAtPath(pageIndex, [], atIndex, makeNewElement(kind));
  }

  /** Insert a leaf-kind element at an arbitrary tree path. */
  insertLeafAtPath(
    pageIndex: number,
    path: ContainerPath,
    atIndex: number,
    kind: NewElementKind,
  ): void {
    this.insertElementAtPath(pageIndex, path, atIndex, makeNewElement(kind));
  }

  /** Insert a bank-linked leaf element at an arbitrary tree path. */
  removeElement(pageIndex: number, elementIndex: number): void {
    this.removeElementAtPath(pageIndex, [], elementIndex);
  }

  duplicateElementAtPath(pageIndex: number, path: ContainerPath, index: number): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const list = getChildrenAtPath(page, path);
    const src = list?.[index];
    // System fields and singleton kinds (program, request category) may never
    // be duplicated — the schema allows at most one per definition. The check
    // is subtree-deep: a field_group/columns wrapping a singleton is equally
    // un-duplicable.
    if (!src || src.kind === "system_field" || subtreeContainsSingleton(src)) return;
    const newCloneId = newId();
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const target = getChildrenAtPath(draftPage, path);
      const source = target?.[index];
      if (!target || !source || source.kind === "system_field") return;
      if (subtreeContainsSingleton(source)) return;
      const dup = clone(source) as TIntakeTreeElement;
      regenerateSubtreeIds(dup, newCloneId);
      annotateDuplicateLabels(dup);
      target.splice(index + 1, 0, dup);
    });
    if (this.draft) {
      const updatedPage = this.draft.pages[pageIndex];
      if (!updatedPage) return;
      const updated = getChildrenAtPath(updatedPage, path);
      const inserted = updated?.[index + 1];
      if (inserted && inserted.id === newCloneId) {
        this.selectedElementId = inserted.id;
      }
    }
  }

  duplicateElement(pageIndex: number, elementIndex: number): void {
    this.duplicateElementAtPath(pageIndex, [], elementIndex);
  }

  /**
   * Duplicates a bank-linked global custom field as a form-specific copy:
   * - Strips `bank_field_id` so the copy is disconnected from the global library
   *   (future changes to the global field will not affect this copy — PRD §2.3.b).
   * - Deep-clones options/validation/description; clears per-option `hidden`
   *   flags (form-specific fields don't have the hide-from-requesters concept).
   * - Appends a localized "(Copy)" suffix to labels; assigns a fresh UUID
   *   `name` so the copy is uniquely identifiable.
   * - Clears `visible_if` to avoid carrying conditional-logic refs that may not
   *   make sense once the copy is form-specific.
   * No-ops for system fields and elements without `bank_field_id`.
   */
  duplicateElementAsFormSpecific(pageIndex: number, elementIndex: number): void {
    this.duplicateElementAsFormSpecificAtPath(pageIndex, [], elementIndex);
  }

  duplicateElementAsFormSpecificAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
  ): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const list = getChildrenAtPath(page, path);
    const src = list?.[index];
    if (!src || src.kind === "system_field") return;
    const bankFieldId = (src as { bank_field_id?: string }).bank_field_id;
    if (!bankFieldId) return;
    const newCloneId = newId();
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const target = getChildrenAtPath(draftPage, path);
      const source = target?.[index];
      if (!target || !source || source.kind === "system_field") return;
      const dup = clone(source) as TIntakeTreeElement;
      dup.id = newCloneId;
      if (dup.kind !== "system_field" && "name" in dup) {
        // Strip the global link so the copy is purely form-specific.
        delete (dup as { bank_field_id?: string }).bank_field_id;
        // Clear conditional logic on the copy — refs to other fields may be
        // intact, but the source rule was authored against the global field's
        // identity and may surprise authors. Safer to drop and let them re-add.
        dup.visible_if = null;
        dup.name = newId();
        if (dup.label) {
          dup.label = {
            en: `${dup.label.en} (Copy)`,
            fr: `${dup.label.fr} (Copie)`,
          };
        }
        // Clear per-option `hidden` flags — bank-only concept (PRD §2.5.b).
        const withOptions = dup as { options?: Array<{ hidden?: boolean }> };
        if (withOptions.options) {
          for (const option of withOptions.options) {
            delete option.hidden;
          }
        }
      }
      target.splice(index + 1, 0, dup);
    });
    if (this.draft) {
      const updatedPage = this.draft.pages[pageIndex];
      if (!updatedPage) return;
      const updated = getChildrenAtPath(updatedPage, path);
      const inserted = updated?.[index + 1];
      if (inserted && inserted.id === newCloneId) {
        this.selectedElementId = inserted.id;
      }
    }
  }

  moveElement(pageIndex: number, fromIndex: number, toIndex: number): void {
    this.moveElementBetweenPaths(pageIndex, [], fromIndex, [], toIndex);
  }

  /**
   * Move an element (preserving its subtree) from a position on one page to
   * the end of another page's root container. System fields and self-page
   * moves are rejected — they are the caller's responsibility to avoid.
   *
   * Names are preserved so cross-page conditional refs continue to resolve.
   */
  moveElementToPage(
    sourcePageIndex: number,
    sourcePath: ContainerPath,
    sourceIndex: number,
    targetPageId: string,
  ): void {
    if (!this.draft) return;
    const sourcePage = this.draft.pages[sourcePageIndex];
    if (!sourcePage) return;
    const sourceList = getChildrenAtPath(sourcePage, sourcePath);
    const src = sourceList?.[sourceIndex];
    if (!src || isSystemFieldElement(src)) return;
    const targetPageIndex = this.draft.pages.findIndex((p) => p.id === targetPageId);
    if (targetPageIndex === -1) return;
    if (sourcePage.id === targetPageId && sourcePath.length === 0) {
      // Same-page root → no-op (existing reorder UI covers in-page moves).
      return;
    }
    const movedId = src.id;
    this.applyDefinitionPatch((draft) => {
      const draftSource = draft.pages[sourcePageIndex];
      if (!draftSource) return;
      const fromList = getChildrenAtPath(draftSource, sourcePath);
      if (!fromList) return;
      const [moved] = fromList.splice(sourceIndex, 1);
      if (!moved) return;
      const draftTarget = draft.pages[targetPageIndex];
      if (!draftTarget) {
        // Restore on the unlikely chance the target vanished mid-mutation.
        fromList.splice(sourceIndex, 0, moved);
        return;
      }
      draftTarget.elements.push(moved);
    });
    this.activePageId = targetPageId;
    this.selectedElementId = movedId;
  }

  /**
   * Duplicate an element (with its subtree) onto the END of another page's
   * root container. Every node in the clone gets a fresh ``id``; leaves get a
   * fresh ``name``. Container ``columns[*].id`` are regenerated too.
   * System fields cannot be duplicated to another page.
   */
  duplicateElementToPage(
    sourcePageIndex: number,
    sourcePath: ContainerPath,
    sourceIndex: number,
    targetPageId: string,
  ): void {
    if (!this.draft) return;
    const sourcePage = this.draft.pages[sourcePageIndex];
    if (!sourcePage) return;
    const sourceList = getChildrenAtPath(sourcePage, sourcePath);
    const src = sourceList?.[sourceIndex];
    // Singleton kinds (program, request category) — including containers that
    // wrap one — may exist at most once per definition, so cross-page
    // duplication is a no-op for them just like in-place duplication. Moving
    // remains allowed (still exactly one instance).
    if (!src || isSystemFieldElement(src) || subtreeContainsSingleton(src)) return;
    const targetPageIndex = this.draft.pages.findIndex((p) => p.id === targetPageId);
    if (targetPageIndex === -1) return;
    const newCloneId = newId();
    this.applyDefinitionPatch((draft) => {
      const draftSource = draft.pages[sourcePageIndex];
      if (!draftSource) return;
      const fromList = getChildrenAtPath(draftSource, sourcePath);
      const source = fromList?.[sourceIndex];
      if (!source || source.kind === "system_field") return;
      const dup = clone(source) as TIntakeTreeElement;
      regenerateSubtreeIds(dup, newCloneId);
      annotateDuplicateLabels(dup);
      const draftTarget = draft.pages[targetPageIndex];
      if (!draftTarget) return;
      draftTarget.elements.push(dup);
    });
    this.activePageId = targetPageId;
    this.selectedElementId = newCloneId;
  }

  setElementFieldAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    updater: (el: TIntakeTreeElement) => void,
  ): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (!page) return;
      const list = getChildrenAtPath(page, path);
      const el = list?.[index];
      if (el) updater(el);
    });
  }

  setElementField(
    pageIndex: number,
    elementIndex: number,
    updater: (el: TIntakeElement) => void,
  ): void {
    this.setElementFieldAtPath(pageIndex, [], elementIndex, (el) => {
      // Top-level setElementField predates containers; the existing callers
      // only operate on leaf TIntakeElement kinds. Safe cast: containers are
      // never reachable through this API.
      if (el.kind === "field_group" || el.kind === "columns") return;
      if (el.kind === "info_text" || el.kind === "rich_text" || el.kind === "divider") return;
      updater(el);
    });
  }

  /**
   * Patch a container element's properties — group title / collapsed, columns
   * shape, info_text content. Targets a container by its location in the tree.
   */
  setContainerProperty(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    updater: (el: TIntakeTreeElement) => void,
  ): void {
    this.setElementFieldAtPath(pageIndex, path, index, updater);
  }

  /**
   * Adjust the column count of a ``columns`` container in 2..4. When growing,
   * new empty column slots are appended; when shrinking, the trailing column
   * children are concatenated onto the previous column so no data is lost.
   */
  setColumnsCount(pageIndex: number, path: ContainerPath, index: number, nextCount: number): void {
    const target = Math.max(2, Math.min(4, nextCount));
    this.setElementFieldAtPath(pageIndex, path, index, (el) => {
      if (el.kind !== "columns") return;
      const current = el.columns.length;
      if (target === current) return;
      if (target > current) {
        for (let i = current; i < target; i += 1) {
          el.columns.push({ id: newId(), children: [] });
        }
        return;
      }
      // Shrinking — fold trailing column children into the last surviving column.
      const survivors = el.columns.slice(0, target);
      const dropped = el.columns.slice(target);
      const lastSurvivor = survivors[survivors.length - 1];
      if (lastSurvivor) {
        for (const col of dropped) lastSurvivor.children.push(...col.children);
      }
      el.columns = survivors;
    });
  }

  private mutateChoiceAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    updater: (el: ChoiceElement) => void,
  ): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (!page) return;
      const list = getChildrenAtPath(page, path);
      const el = list?.[index];
      if (isChoiceElement(el)) updater(el);
    });
  }

  addOptionAtPath(pageIndex: number, path: ContainerPath, index: number): void {
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      el.options.push(makeOption(el.options.length));
    });
  }

  addOption(pageIndex: number, elementIndex: number): void {
    this.addOptionAtPath(pageIndex, [], elementIndex);
  }

  removeOptionAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    optionIndex: number,
  ): void {
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      el.options.splice(optionIndex, 1);
    });
  }

  removeOption(pageIndex: number, elementIndex: number, optionIndex: number): void {
    this.removeOptionAtPath(pageIndex, [], elementIndex, optionIndex);
  }

  moveOptionAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    fromIndex: number,
    toIndex: number,
  ): void {
    if (fromIndex === toIndex) return;
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      const [moved] = el.options.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(toIndex, el.options.length));
      el.options.splice(insertAt, 0, moved);
    });
  }

  moveOption(pageIndex: number, elementIndex: number, fromIndex: number, toIndex: number): void {
    this.moveOptionAtPath(pageIndex, [], elementIndex, fromIndex, toIndex);
  }

  setOptionFieldAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    optionIndex: number,
    updater: (option: TIntakeChoiceOption) => void,
  ): void {
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      const option = el.options[optionIndex];
      if (option) updater(option);
    });
  }

  setOptionField(
    pageIndex: number,
    elementIndex: number,
    optionIndex: number,
    updater: (option: TIntakeChoiceOption) => void,
  ): void {
    this.setOptionFieldAtPath(pageIndex, [], elementIndex, optionIndex, updater);
  }

  /** Persist the form. ``metaOnly`` omits the ``definition`` from the PATCH:
   *  a Form Workflow form's page content is owned + persisted by the workflow
   *  store (#2163), so the legacy PATCH must not send the definition (it would
   *  clobber it) — but the form META (name / teams / default request category /
   *  default request owner) still lives on this legacy PATCH. Saving meta-only
   *  rather than skipping the save entirely is what lets a workflow-created form
   *  set its required category/owner so it can publish. */
  async save(slug: string, options: { metaOnly?: boolean } = {}): Promise<void> {
    const run = this.performSave(slug, options);
    this.saveInFlight = run;
    try {
      await run;
    } finally {
      // Post-`await` code runs outside the action `save` opened, so both the
      // read and the write of `saveInFlight` need their own action.
      runInAction(() => {
        if (this.saveInFlight === run) this.saveInFlight = null;
      });
    }
  }

  /** Await an in-flight PATCH, then persist if the draft is still dirty.
   *  Publish calls this so a click inside the 1000ms autosave debounce
   *  cannot POST /publish/ before the meta PATCH lands. Throws on failure. */
  async flushPendingSave(slug: string, options: { metaOnly?: boolean } = {}): Promise<void> {
    if (this.saveInFlight) await this.saveInFlight;
    if (this.isDirty) await this.save(slug, options);
  }

  private async performSave(slug: string, options: { metaOnly?: boolean } = {}): Promise<void> {
    if (!this.form || !this.draft) return;
    const metaOnly = options.metaOnly ?? false;
    runInAction(() => {
      this.isSaving = true;
    });
    this.clearErrors();
    // Snapshot the editable state we're sending. Setters reassign ``topLevel``
    // and ``draft``, so a changed identity on return means the user kept
    // editing while the PATCH was in flight — the server echo is then stale and
    // must not clobber the newer local value (the autosave letter-drop bug).
    const topLevelAtSave = this.topLevel;
    const draftAtSave = this.draft;
    const formId = this.form.id;
    const body = {
      // The workflow store owns the definition for a workflow form; never send
      // it meta-only or it would overwrite the workflow-authored content.
      ...(metaOnly ? {} : { definition: this.draft }),
      name: this.topLevel.name,
      // Normalized on the way OUT only. Tiptap leaves `<p></p>` behind for a
      // description the user emptied, and persisting that reads as content:
      // it would render a padded empty block on the requester's form and show
      // as a pending change after a bare focus/blur. The raw editor buffer
      // stays in `topLevel` because `normalizeRichText` must never be fed back
      // to the editor as its `value` (NG-265) — the builder does exactly that.
      description_html: normalizeRichText(this.topLevel.descriptionHtml),
      // No ``is_active``: it is a live switch that ``toggleFormActive`` writes on
      // its own, not editorial draft content. Echoing it from this snapshot let
      // a debounced autosave revert an activation made after the builder loaded
      // (a toggle while dirty, another tab, an API publish), leaving the form
      // inactive so its published workflow 404'd for requesters (NG-1211).
      teams: this.topLevel.teams,
      request_type: this.topLevel.request_type,
      default_request_category_value_id: this.topLevel.default_request_category_value_id,
      default_request_owner_ids: this.topLevel.default_request_owner_ids,
      managing_team_id: this.topLevel.managingTeamId,
    };
    // What this save changed for `form_updated` (NG-2058): the body carries every
    // field on every save, so compare it with the stored form, read the way
    // `hydrateFromForm` reads it so a default the builder filled in doesn't count.
    const fields = changedFields(body, savedFormBody(this.form));
    try {
      const next = await this.rootStore.intakeService.updateForm(slug, formId, body);
      runInAction(() => {
        this.forms = this.forms.map((f) => (f.id === next.id ? withResolvedCanEdit(next, f) : f));
        const editedDuringSave = this.topLevel !== topLevelAtSave || this.draft !== draftAtSave;
        if (editedDuringSave) {
          // Keep the user's in-progress edits; refresh only server-owned meta.
          // ``_dirty`` stays true so the next debounced save persists them.
          this.applyServerMeta(next);
        } else if (metaOnly) {
          // Meta-only never sent the draft, so re-hydrating from the echo would
          // needlessly reset the builder view. Refresh server-owned meta and
          // clear dirty — this save captured everything, so the autosave loop
          // must not re-fire (``applyServerMeta`` alone leaves ``_dirty`` set).
          this.applyServerMeta(next);
          this._dirty = false;
        } else {
          this.hydrateFromForm(next);
        }
      });
      if (fields.length > 0) track("form_updated", { form_id: formId, fields, source: "page" });
    } catch (err) {
      // Pass the in-memory draft so the parser can resolve nested element
      // paths (``children``/``columns`` segments) back to a stable id. The
      // inline-error filter then targets the right row regardless of depth.
      const { inline, banner } = parseSaveErrors(err, this.draft);
      runInAction(() => {
        this.inlineErrors = inline;
        this.bannerErrors = banner;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  /** Push the current draft live. Backend gates (must-be-active +
   *  required-fields-blank + strict definition schema) are also enforced
   *  by ``publishDisabledReason`` so the FE rarely fires this against a
   *  state that will 409/422. */
  async publishForm(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    if (!this.form) return;
    runInAction(() => {
      this.isPublishing = true;
    });
    this.clearErrors();
    try {
      const next = await this.rootStore.intakeService.publishForm(slug, this.form.id);
      runInAction(() => {
        this.forms = this.forms.map((f) => (f.id === next.id ? withResolvedCanEdit(next, f) : f));
        this.hydrateFromForm(next);
      });
      track("form_published", { form_id: next.id, source });
    } catch (err) {
      runInAction(() => {
        // `this.draft` is read inside the action too: this runs after an
        // `await`, i.e. outside any reaction, where a bare read trips mobx's
        // `observableRequiresReaction` guard.
        const { inline, banner } = parseSaveErrors(err, this.draft);
        this.inlineErrors = inline;
        this.bannerErrors = banner;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isPublishing = false;
      });
    }
  }

  /** Drop the unpublished draft and re-hydrate from the last published
   *  snapshot. The form list / builder visibly snap back to live. */
  async discardDraft(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    if (!this.form) return;
    runInAction(() => {
      this.isSaving = true;
    });
    try {
      const next = await this.rootStore.intakeService.discardFormDraft(slug, this.form.id);
      runInAction(() => {
        this.forms = this.forms.map((f) => (f.id === next.id ? withResolvedCanEdit(next, f) : f));
        this.hydrateFromForm(next);
        this._dirty = false;
      });
      track("form_draft_discarded", { form_id: next.id, source });
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  async toggleFormActive(
    slug: string,
    formId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const target = this.forms.find((f) => f.id === formId);
    if (!target) return;
    const previous = target.is_active;
    const next = !previous;
    runInAction(() => {
      this.forms = this.forms.map((f) => (f.id === formId ? { ...f, is_active: next } : f));
      // Unconditional: the autosave never sends ``is_active``, so a dirty draft
      // cannot overwrite it and the switch must show what was just written.
      if (this.activeFormId === formId && this.form) {
        this.topLevel.is_active = next;
      }
    });
    try {
      const updated = await this.rootStore.intakeService.updateForm(slug, formId, {
        is_active: next,
      });
      runInAction(() => {
        this.forms = this.forms.map((f) => (f.id === formId ? withResolvedCanEdit(updated, f) : f));
      });
      track(next ? "form_activated" : "form_deactivated", { form_id: formId, source });
    } catch (err) {
      runInAction(() => {
        this.forms = this.forms.map((f) => (f.id === formId ? { ...f, is_active: previous } : f));
        if (this.activeFormId === formId) {
          this.topLevel.is_active = previous;
        }
      });
      throw err;
    }
  }

  async deleteForm(slug: string, formId: string, source: AnalyticsSource = "page"): Promise<void> {
    await this.rootStore.intakeService.deleteForm(slug, formId);
    track("form_deleted", { form_id: formId, source });
    runInAction(() => {
      this.forms = this.forms.filter((f) => f.id !== formId);
      if (this.activeFormId === formId) {
        const next = this.forms[0] ?? null;
        if (next) {
          this.hydrateFromForm(next);
        } else {
          this.activeFormId = null;
          this.draft = null;
          this.topLevel = emptyTopLevel();
        }
      }
    });
  }

  setActiveForm(formId: string): void {
    const cached = this.forms.find((f) => f.id === formId);
    if (cached) {
      // Render from the cache immediately — no skeleton for a workflow we already have.
      this.hydrateFromForm(cached);
    } else {
      this.activeFormId = formId;
    }
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return;
    if (!cached) this.isLoading = true;
    // The detail read is the ONLY authority on ``can_edit`` (#3144 D7) and it runs on
    // EVERY activation — a cached boolean never suppresses it. Editability is not a
    // property of the workflow, it is a property of (caller, workflow) that turns over
    // the moment the workflow's managing team changes: a cached ``true`` outlives an
    // owner reassignment as an editor that 403s on save, and a cached ``false`` outlives
    // it as a permanent lock on a workflow the caller now owns. One GET per activation
    // is the price of neither.
    this.canEditError = null;
    this.isResolvingCanEdit = true;
    void Promise.resolve(this.rootStore.intakeService.getForm(slug, formId, { includeDraft: true }))
      .then((form) => {
        // A response that isn't a form must not blank a page that is currently fine —
        // but it did not answer the editability question either, so it is a failed
        // resolution, not a silent no-op (the surface falls back to read-only).
        if (!form || typeof form !== "object") {
          console.warn("intake: the workflow detail read returned no form", { slug, formId });
          runInAction(() => {
            // Only worth reporting as an editability failure when there is a surface to
            // disarm: with nothing cached the page has no workflow on it at all, and
            // "we couldn't check whether you can edit this" on top of the load error
            // describes the smaller half of what went wrong.
            if (this.activeFormId === formId && cached) {
              this.canEditError = "The workflow detail read returned no form.";
            }
          });
          return;
        }
        runInAction(() => {
          // Cache the fresh row whichever workflow is on screen by now, but only
          // re-hydrate the editor for the one the user is actually looking at: two
          // activations in flight (list → detail → back) must not let the slower
          // response repaint the newer workflow.
          this.forms = [form, ...this.forms.filter((f) => f.id !== form.id)];
          if (this.activeFormId === formId) this.hydrateFromForm(form);
        });
      })
      .catch((err: unknown) => {
        // The form id is what makes this actionable — the slug alone does not say which
        // workflow the user is now looking at read-only.
        console.error("intake: could not resolve the workflow's editability", {
          slug,
          formId,
          err,
        });
        runInAction(() => {
          if (this.activeFormId !== formId) return;
          if (cached) {
            // Keep rendering the workflow — blanking a page that is otherwise fine is
            // worse — but DISARM it: we no longer know whether this caller may save, so
            // the surface must not keep offering to. The route surfaces the error.
            this.canEditError = (err as Error).message;
            return;
          }
          this.loadError = (err as Error).message;
        });
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
          if (this.activeFormId === formId) this.isResolvingCanEdit = false;
        });
      });
  }

  /**
   * Keep the shared ``forms`` cache in sync when a component that owns its own
   * paginated list (e.g. the settings forms table) mutates a form. No-ops if
   * the form was never loaded into this store's flat list.
   */
  applyFormUpdate(updated: TIntakeForm): void {
    runInAction(() => {
      if (this.forms.some((f) => f.id === updated.id)) {
        this.forms = this.forms.map((f) =>
          f.id === updated.id ? withResolvedCanEdit(updated, f) : f,
        );
      }
    });
  }

  removeForm(formId: string): void {
    runInAction(() => {
      this.forms = this.forms.filter((f) => f.id !== formId);
    });
  }

  setSubmissionsFormFilter(value: readonly string[]): void {
    this.submissionsFormFilter = [...value];
  }

  setSubmissionsProgramFilter(value: readonly string[]): void {
    this.submissionsProgramFilter = [...value];
  }

  setSubmissionsSearch(value: string): void {
    this.submissionsSearch = value;
  }

  setSubmissionsStatusFilter(value: readonly string[]): void {
    this.submissionsStatusFilter = [...value];
  }

  setSubmissionsRequestedByFilter(value: readonly string[]): void {
    this.submissionsRequestedByFilter = [...value];
  }

  setSubmissionsOwnerFilter(value: readonly string[]): void {
    this.submissionsOwnerFilter = [...value];
  }

  setSubmissionsTeamFilter(value: readonly string[]): void {
    this.submissionsTeamFilter = [...value];
  }

  setSubmissionsCategoryFilter(value: readonly string[]): void {
    this.submissionsCategoryFilter = [...value];
  }

  setSubmissionsSlaFilter(value: readonly string[]): void {
    this.submissionsSlaFilter = [...value];
  }

  setSubmissionsPriorityFilter(value: readonly string[]): void {
    this.submissionsPriorityFilter = [...value];
  }

  setSubmissionsCreatedAfter(value: string): void {
    this.submissionsCreatedAfter = value;
  }

  setSubmissionsCreatedBefore(value: string): void {
    this.submissionsCreatedBefore = value;
  }

  setSubmissionsUpdatedAfter(value: string): void {
    this.submissionsUpdatedAfter = value;
  }

  setSubmissionsUpdatedBefore(value: string): void {
    this.submissionsUpdatedBefore = value;
  }

  /** Reset every column-header filter (and the search box) to its empty
   *  state, so a mount / workspace switch starts on the full unfiltered list.
   *  Called by `hydrateSubmissionsPrefs` as the base it restores onto — never
   *  from the list effect directly, which would clear OUTSIDE the write-back
   *  guard and let the persistence reaction wipe the key being restored. */
  clearSubmissionsFilters(): void {
    this.submissionsFormFilter = [];
    this.submissionsProgramFilter = [];
    this.submissionsSearch = "";
    this.submissionsStatusFilter = [];
    this.submissionsRequestedByFilter = [];
    this.submissionsOwnerFilter = [];
    this.submissionsTeamFilter = [];
    this.submissionsCategoryFilter = [];
    this.submissionsSlaFilter = [];
    this.submissionsPriorityFilter = [];
    this.submissionsCreatedAfter = "";
    this.submissionsCreatedBefore = "";
    this.submissionsUpdatedAfter = "";
    this.submissionsUpdatedBefore = "";
    this.submissionsGroupBy = "none";
    this.submissionsGroups = [];
    // Reset with the rest: a view that does not narrow the archive must not
    // inherit "archived only" from whatever the user was last looking at.
    this.submissionsArchivedView = "active";
  }

  /**
   * The current submissions filter set, in the shape a saved view stores.
   *
   * The read counterpart of `hydrateSubmissionsFilters`; both live here so the
   * field list stays in one file rather than one copy per consumer.
   */
  get submissionsFilterState(): RequestViewFilterState {
    return {
      search: this.submissionsSearch,
      formIds: [...this.submissionsFormFilter],
      programIds: [...this.submissionsProgramFilter],
      statusIds: [...this.submissionsStatusFilter],
      requestedByIds: [...this.submissionsRequestedByFilter],
      ownerIds: [...this.submissionsOwnerFilter],
      teamIds: [...this.submissionsTeamFilter],
      categoryIds: [...this.submissionsCategoryFilter],
      slaStates: [...this.submissionsSlaFilter],
      priorities: [...this.submissionsPriorityFilter],
      createdAfter: this.submissionsCreatedAfter,
      createdBefore: this.submissionsCreatedBefore,
      updatedAfter: this.submissionsUpdatedAfter,
      updatedBefore: this.submissionsUpdatedBefore,
      archivedView: this.submissionsArchivedView,
      groupBy: this.submissionsGroupBy,
    };
  }

  /**
   * REPLACE the whole submissions filter set with a saved view's state.
   *
   * A full replace, not a merge, and that is the point: this store is a singleton
   * shared by the plain Requests list and every request-view page, so a merge would
   * let one surface's filters survive a navigation into the other. Clearing first
   * means a mount is always the only thing deciding what is filtered (NG-528).
   *
   * INVARIANT: keep this covering every field `clearSubmissionsFilters` resets —
   * a field cleared here but not assigned silently drops that filter when a view
   * opens, and one assigned but not cleared leaks into the next surface.
   *
   * Dropping `submissionsPrefsSlug` is what keeps a view's filters out of the
   * plain list's persisted prefs (NG-517 review). The persistence reactions
   * flush when the OUTERMOST action ends and read the slug then — so nulling it
   * here means this seed, and every filter/sort change made while the view owns
   * the store, has no key to write under. The next plain-list mount re-arms
   * persistence via `hydrateSubmissionsPrefs`, which also re-reads the stored
   * set this method just displaced.
   */
  hydrateSubmissionsFilters(state: RequestViewFilterState): void {
    this.submissionsPrefsSlug = null;
    this.clearSubmissionsFilters();
    this.submissionsSearch = state.search;
    this.submissionsFormFilter = [...state.formIds];
    // ``?? []``: a view saved before NG-1631 has no ``programIds`` key.
    this.submissionsProgramFilter = [...(state.programIds ?? [])];
    this.submissionsStatusFilter = [...state.statusIds];
    this.submissionsRequestedByFilter = [...state.requestedByIds];
    this.submissionsOwnerFilter = [...state.ownerIds];
    this.submissionsTeamFilter = [...state.teamIds];
    this.submissionsCategoryFilter = [...state.categoryIds];
    this.submissionsSlaFilter = [...state.slaStates];
    this.submissionsPriorityFilter = [...state.priorities];
    this.submissionsCreatedAfter = state.createdAfter;
    this.submissionsCreatedBefore = state.createdBefore;
    this.submissionsUpdatedAfter = state.updatedAfter;
    this.submissionsUpdatedBefore = state.updatedBefore;
    this.submissionsArchivedView = state.archivedView;
    this.submissionsGroupBy = state.groupBy;
  }

  // ---- DataTable bridge ----

  /** Bridges the flat submissions filter fields to the TanStack
   *  ColumnFiltersState the shared DataTable consumes. Column ids match the
   *  ColumnDef ids in intake-submissions-columns.tsx. */
  get submissionsColumnFilters(): ColumnFiltersState {
    // Search is deliberately NOT a columnFilters entry — it stays a top-level
    // toolbar input (spec §14, parity with the legacy table). Only column-header
    // filters live here.
    const out: ColumnFiltersState = [];
    if (this.submissionsStatusFilter.length > 0) {
      out.push({
        id: "status",
        value: { type: "multi", value: [...this.submissionsStatusFilter] },
      });
    }
    if (this.submissionsRequestedByFilter.length > 0) {
      out.push({
        id: "requestedBy",
        value: { type: "multi", value: [...this.submissionsRequestedByFilter] },
      });
    }
    if (this.submissionsOwnerFilter.length > 0) {
      out.push({ id: "owner", value: { type: "multi", value: [...this.submissionsOwnerFilter] } });
    }
    if (this.submissionsTeamFilter.length > 0) {
      out.push({ id: "teams", value: { type: "multi", value: [...this.submissionsTeamFilter] } });
    }
    if (this.submissionsFormFilter.length > 0) {
      out.push({
        id: "formUsed",
        value: { type: "multi", value: [...this.submissionsFormFilter] },
      });
    }
    if (this.submissionsProgramFilter.length > 0) {
      out.push({
        id: "programs",
        value: { type: "multi", value: [...this.submissionsProgramFilter] },
      });
    }
    if (this.submissionsCategoryFilter.length > 0) {
      out.push({
        id: "requestCategory",
        value: { type: "multi", value: [...this.submissionsCategoryFilter] },
      });
    }
    if (this.submissionsSlaFilter.length > 0) {
      out.push({ id: "sla", value: { type: "multi", value: [...this.submissionsSlaFilter] } });
    }
    if (this.submissionsPriorityFilter.length > 0) {
      out.push({
        id: "priority",
        value: { type: "multi", value: [...this.submissionsPriorityFilter] },
      });
    }
    if (this.submissionsCreatedAfter || this.submissionsCreatedBefore) {
      out.push({
        id: "created",
        value: {
          type: "dateRange",
          value: { from: this.submissionsCreatedAfter, to: this.submissionsCreatedBefore },
        },
      });
    }
    if (this.submissionsUpdatedAfter || this.submissionsUpdatedBefore) {
      out.push({
        id: "lastUpdated",
        value: {
          type: "dateRange",
          value: { from: this.submissionsUpdatedAfter, to: this.submissionsUpdatedBefore },
        },
      });
    }
    return out;
  }

  /** Fans a TanStack ColumnFiltersState back into the flat filter fields
   *  (facade) and refetches page 1 so the cursor window matches the new set.
   *  Filters absent from ``next`` are cleared. */
  setSubmissionsColumnFilters(next: ColumnFiltersState, slug: string): void {
    // Persistence is handled by `watchSubmissionsListPrefs`, not here — see why
    // there.
    this.applySubmissionsColumnFilters(next);
    void this.fetchSubmissions(slug);
  }

  /** Apply a filter set to the flat fields. No fetch and no persistence of its
   *  own, so hydration can reuse it without writing back what it just read. */
  private applySubmissionsColumnFilters(next: ColumnFiltersState): void {
    // Search is owned by the toolbar input, not the column-header filters, so
    // it is intentionally left untouched here (see submissionsColumnFilters).
    const find = (id: string) =>
      next.find((f) => f.id === id)?.value as
        | { type: "multi"; value: string[] }
        | { type: "dateRange"; value: { from: string; to: string } }
        | undefined;

    this.submissionsStatusFilter = (find("status") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsRequestedByFilter =
      (find("requestedBy") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsOwnerFilter = (find("owner") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsTeamFilter = (find("teams") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsFormFilter = (find("formUsed") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsProgramFilter =
      (find("programs") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsCategoryFilter =
      (find("requestCategory") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsSlaFilter = (find("sla") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsPriorityFilter =
      (find("priority") as { value: string[] } | undefined)?.value ?? [];
    const created = find("created") as { value: { from: string; to: string } } | undefined;
    this.submissionsCreatedAfter = created?.value.from ?? "";
    this.submissionsCreatedBefore = created?.value.to ?? "";
    const updated = find("lastUpdated") as { value: { from: string; to: string } } | undefined;
    this.submissionsUpdatedAfter = updated?.value.from ?? "";
    this.submissionsUpdatedBefore = updated?.value.to ?? "";
  }

  /** Stores the TanStack sort, resolves the whitelisted server token, and
   *  refetches page 1. Non-sortable / unknown column ids clear the token. */
  setSubmissionsSorting(next: SortingState, slug: string): void {
    // Persistence is handled by `watchSubmissionsListPrefs`, not here.
    this.applySubmissionsSorting(next);
    void this.fetchSubmissions(slug);
  }

  /** Sort state + resolved server token, without the fetch — so hydration can
   *  restore a stored sort into the query the mount fetch is about to build. */
  private applySubmissionsSorting(next: SortingState): void {
    this.submissionsSorting = next;
    const first = next[0];
    const token = first ? SUBMISSIONS_COLUMN_TO_ORDER_BY[first.id] : undefined;
    this.submissionsOrderBy = token ? `${first.desc ? "-" : ""}${token}` : "";
  }

  setSubmissionsRowSelection(next: RowSelectionState): void {
    this.submissionsRowSelection = next;
  }

  /** Switch the table's server-side grouping dimension and refetch page 1
   *  (re-ordered so the chosen group's rows are contiguous). */
  setSubmissionsGroupBy(next: SubmissionGroupBy, slug: string): void {
    this.submissionsGroupBy = next;
    if (next === "none") this.submissionsGroups = [];
    void this.fetchSubmissions(slug);
  }

  /** Group-by override for the table list fetch only. Returns ``undefined``
   *  when ungrouped so the param is omitted, and so the board/export callers
   *  (which don't pass it) never group. */
  private groupByOverride(): Partial<TIntakeSubmissionsListQuery> | undefined {
    return this.submissionsGroupBy === "none" ? undefined : { group_by: this.submissionsGroupBy };
  }

  private buildSubmissionsListQuery(
    cursor: string | null,
    overrides?: Partial<TIntakeSubmissionsListQuery>,
  ): TIntakeSubmissionsListQuery {
    const query: TIntakeSubmissionsListQuery = {};
    if (this.submissionsFormFilter.length > 0) query.form = [...this.submissionsFormFilter];
    if (this.submissionsProgramFilter.length > 0) {
      query.program = [...this.submissionsProgramFilter];
    }
    const q = this.submissionsSearch.trim();
    if (q) query.q = q;
    if (this.submissionsStatusFilter.length > 0) query.status = [...this.submissionsStatusFilter];
    if (this.submissionsRequestedByFilter.length > 0)
      query.requested_by = [...this.submissionsRequestedByFilter];
    if (this.submissionsOwnerFilter.length > 0) query.owner = [...this.submissionsOwnerFilter];
    if (this.submissionsTeamFilter.length > 0) query.team = [...this.submissionsTeamFilter];
    if (this.submissionsCategoryFilter.length > 0)
      query.category = [...this.submissionsCategoryFilter];
    if (this.submissionsSlaFilter.length > 0) query.sla_state = [...this.submissionsSlaFilter];
    if (this.submissionsPriorityFilter.length > 0)
      query.priority_tier = [...this.submissionsPriorityFilter];
    if (this.submissionsCreatedAfter) query.submitted_after = this.submissionsCreatedAfter;
    if (this.submissionsCreatedBefore) query.submitted_before = this.submissionsCreatedBefore;
    if (this.submissionsUpdatedAfter) query.updated_after = this.submissionsUpdatedAfter;
    if (this.submissionsUpdatedBefore) query.updated_before = this.submissionsUpdatedBefore;
    // "active" is the server default; only send the param when narrowing away
    // from it to keep query strings (and cache keys) minimal.
    if (this.submissionsArchivedView !== "active") query.archived = this.submissionsArchivedView;
    if (this.submissionsOrderBy) query.order_by = this.submissionsOrderBy;
    if (cursor) query.cursor = cursor;
    // Board columns override ``state`` (pin to one status) and ``per_page`` (the
    // per-column page size) on top of the shared list/search/archive filters.
    return { ...query, ...overrides };
  }

  /** :meth:`buildSubmissionsListQuery` for the table and board reads, which render
   *  the form's name and nothing else from ``form_detail``: ``lean`` drops each row's
   *  form definition, tens of KB a row, from the response (NG-2424). The export keeps
   *  the plain builder, since its endpoint takes no such param. */
  private leanListQuery(
    cursor: string | null,
    overrides?: Partial<TIntakeSubmissionsListQuery>,
  ): TIntakeSubmissionsListQuery {
    return { ...this.buildSubmissionsListQuery(cursor, overrides), lean: true };
  }

  /**
   * Export the submissions list as CSV. Scoped to the current filters/sort;
   * ``ids`` (selected rows) restrict the export when present. Returns the raw
   * Response so the caller can stream the file (200) or poll the token (202).
   */
  exportSubmissionsCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.intakeService.exportSubmissions(
      slug,
      body,
      this.buildSubmissionsListQuery(null),
    );
  }

  /** Poll an async export by token until its presigned URL is ready (or null). */
  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.intakeService.getExportStatus(slug, tok),
    );
  }

  /** Replaces ``submissions`` with the first page using the current filter
   *  state. Resets the cursor so a follow-up ``fetchMoreSubmissions`` walks
   *  from the start of the (possibly newly filtered) result set. */
  async fetchSubmissions(slug: string): Promise<void> {
    const seq = ++this.submissionsSeq;
    const requestedAt = this.submissionWriteClock;
    runInAction(() => {
      this.isLoadingSubmissions = true;
      this.submissionsError = null;
      this.submissionsDenied = false;
    });
    try {
      const page = await this.rootStore.intakeService.listSubmissions(
        slug,
        this.leanListQuery(null, this.groupByOverride()),
      );
      // Superseded by a newer fetch: these rows answer an older filter set, and
      // writing them would overwrite the newer results whenever the older
      // request happens to resolve last (NG-1724).
      if (seq !== this.submissionsSeq) return;
      runInAction(() => {
        this.submissions = this.keepRowsWrittenSince(page.results, requestedAt);
        this.submissionsCursor = page.next_page_results ? page.next_cursor : null;
        this.submissionsHasMore = page.next_page_results;
        this.submissionsTotalCount = page.total_count;
        this.submissionsGroups = page.extra_stats ?? [];
      });
      // Folded-in pending transitions seed the workflow store so the status
      // cells don't fire a per-row pending-transitions request (#2069).
      this.rootStore.requestWorkflow.seedPendingForSubmissions(page.results);
    } catch (err) {
      if (seq !== this.submissionsSeq) {
        // The newer fetch owns the error state; this failure no longer
        // describes what the list shows.
        console.warn("[intake] superseded submissions load failed", { slug }, err);
        return;
      }
      console.warn("[intake] load submissions failed", { slug }, err);
      runInAction(() => {
        this.submissionsError = (err as Error).message;
        this.submissionsDenied = isPermissionError(err);
      });
    } finally {
      // A superseded fetch must not clear the loading flag while the newer
      // one is still in flight.
      if (seq === this.submissionsSeq) {
        runInAction(() => {
          this.isLoadingSubmissions = false;
        });
      }
    }
  }

  /** Load the current user's "awaiting my review" queue. The endpoint
   *  (BE #2170) already scopes to the caller's pending reviews, so there is no
   *  client-side filter; it returns an empty list when the runtime is off. */
  async fetchPendingReviews(slug: string): Promise<void> {
    runInAction(() => {
      this.isLoadingPendingReviews = true;
      this.pendingReviewsError = null;
    });
    try {
      const { requests } = await this.rootStore.intakeService.listPendingReviews(slug);
      runInAction(() => {
        this.pendingReviews = requests;
      });
    } catch (err) {
      runInAction(() => {
        this.pendingReviewsError = (err as Error).message;
      });
    } finally {
      runInAction(() => {
        this.isLoadingPendingReviews = false;
        this.hasLoadedPendingReviews = true;
      });
    }
  }

  /** Remove a resolved request from the reviewer queue so a decided / returned
   *  request drops out of "My reviews" without a full refetch (the endpoint no
   *  longer returns it either — this just keeps the view immediate). */
  /**
   * Put the server's copy of a request into every cache that already holds it.
   *
   * Extracted because a decision is now reachable from the requests LIST as well
   * as the reviewer queue (NG-494): dropping the queue entry alone left the
   * decided row on screen with its old status and its review pill until a manual
   * refresh. Rows the caches don't hold are left alone — this reconciles, it does
   * not insert.
   */
  private replaceSubmissionEverywhere(updated: TIntakeSubmission): void {
    runInAction(() => {
      this.submissions = this.submissions.map((s) => (s.id === updated.id ? updated : s));
      for (const [statusId, col] of this.boardColumns) {
        if (!col.submissions.some((s) => s.id === updated.id)) continue;
        this.boardColumns.set(statusId, {
          ...col,
          submissions: col.submissions.map((s) => (s.id === updated.id ? updated : s)),
        });
      }
    });
  }

  private dropPendingReview(submissionId: string): void {
    runInAction(() => {
      this.pendingReviews = this.pendingReviews.filter((r) => r.id !== submissionId);
    });
  }

  /**
   * The current stage of a workflow request (BE #2169 + #2254) — feeds the
   * reviewer decision surface with the current Review's decision options and
   * whether the caller may decide. Throws on failure; the caller owns UI state.
   */
  async fetchWorkflowStage(
    slug: string,
    submissionId: string,
    view?: TRequestViewSource,
  ): Promise<TWorkflowCurrentStage> {
    const stage = await this.rootStore.intakeService.getWorkflowCurrentStage(slug, submissionId);
    // Only the surface that opens the request passes ``view``; the detail
    // page's inline panel reads the stage too, and its page view is already
    // counted by ``fetchSubmission``.
    if (view) track("request_viewed", { request_id: submissionId, ...view });
    return stage;
  }

  /**
   * Submit the Page a request is waiting on and return the stage it moves into
   * (NG-368). Throws on failure; the caller owns UI state, mirroring
   * {@link fetchWorkflowStage}.
   */
  async submitWorkflowPage(
    slug: string,
    submissionId: string,
    fieldValues: Record<string, unknown>,
    source = "page",
  ): Promise<TWorkflowCurrentStage> {
    const stage = await this.rootStore.intakeService.submitWorkflowPage(
      slug,
      submissionId,
      fieldValues,
    );
    track("intake_workflow_page_submitted", { request_id: submissionId, source });
    return stage;
  }

  /**
   * Explicitly complete the Page a request is waiting on and return the stage
   * it moves into (NG-703). No field values travel with it — since NG-2537 the
   * backend checks the page's visible required fields against the run's saved
   * values instead, rejecting a blank one with a 400. Throws on failure; the
   * caller owns UI state, mirroring {@link submitWorkflowPage}.
   */
  async completeWorkflowStep(
    slug: string,
    submissionId: string,
    source = "page",
  ): Promise<TWorkflowCurrentStage> {
    const stage = await this.rootStore.intakeService.completeWorkflowStep(slug, submissionId);
    track("intake_workflow_step_completed", { request_id: submissionId, source });
    return stage;
  }

  /**
   * Record a reviewer's decision on a request's current Review (BE #2170) and
   * drop it from the reviewer queue. Rethrows (e.g. a 409 first-responder race)
   * so the caller can surface it.
   */
  async submitWorkflowDecision(
    slug: string,
    submissionId: string,
    decisionOptionId: string,
    reason?: TStatusReasonSelection,
    source = "page",
  ): Promise<TWorkflowDecisionResult> {
    const result = await this.rootStore.intakeService.submitWorkflowDecision(
      slug,
      submissionId,
      decisionOptionId,
      reason,
    );
    this.dropPendingReview(submissionId);
    // The decided request goes back into every cache that holds it, so the row
    // updates wherever it is displayed rather than each call site wiring its own
    // refetch — the requests list had none, so its row went stale.
    if (result.request) this.replaceSubmissionEverywhere(result.request);
    track("intake_workflow_decision_submitted", { request_id: submissionId, source });
    return result;
  }

  /**
   * Send a request paused on a Review back to its previous step (BE #2254) and
   * drop it from the reviewer queue. Rethrows so the caller can surface a race.
   *
   * **No UI calls this today, on purpose.** The "Return to previous step" button
   * was removed from the reviewer decision dialog in NG-494, and no other
   * reviewer surface offers the action — the request-detail inline panel only
   * ever had a decision select. Kept because the endpoint is live and the
   * capability is real (it reverts the run's position AND its status, records a
   * ``returned`` transition and notifies the owner — a round trip, not a
   * decline), so re-adding a trigger is a UI change rather than a rebuild.
   */
  async returnWorkflowReview(
    slug: string,
    submissionId: string,
    source = "page",
  ): Promise<TWorkflowReturnResult> {
    const result = await this.rootStore.intakeService.returnWorkflowToPreviousStep(
      slug,
      submissionId,
    );
    this.dropPendingReview(submissionId);
    track("intake_workflow_review_returned", { request_id: submissionId, source });
    return result;
  }

  /** Appends the next page to ``submissions``. No-op when no cursor or a
   *  load-more is already in flight (the page wires this to an intersection
   *  observer that may fire repeatedly while the sentinel is on-screen). */
  async fetchMoreSubmissions(slug: string): Promise<void> {
    if (!this.submissionsHasMore || !this.submissionsCursor) return;
    if (this.isLoadingMoreSubmissions) return;
    // A page-1 fetch in flight is about to replace the list, so the cursor in
    // hand walks a list that is on its way out (NG-1724).
    if (this.isLoadingSubmissions) return;
    const seq = this.submissionsSeq;
    runInAction(() => {
      this.isLoadingMoreSubmissions = true;
    });
    try {
      const page = await this.rootStore.intakeService.listSubmissions(
        slug,
        this.leanListQuery(this.submissionsCursor, this.groupByOverride()),
      );
      // A fresh page-1 fetch started while this page was in flight: it is the
      // next page of the OLD list and must not be appended onto the new one.
      if (seq !== this.submissionsSeq) return;
      runInAction(() => {
        // Guard against duplicate ids if a row was patched into the cache
        // between fetches (e.g. fetchSubmission appending an updated row).
        const known = new Set(this.submissions.map((s) => s.id));
        const fresh = page.results.filter((s) => !known.has(s.id));
        this.submissions = [...this.submissions, ...fresh];
        this.submissionsCursor = page.next_page_results ? page.next_cursor : null;
        this.submissionsHasMore = page.next_page_results;
        this.submissionsTotalCount = page.total_count;
        // Counts are complete + stable across pages; refresh in case a
        // concurrent mutation shifted a group total.
        this.submissionsGroups = page.extra_stats ?? [];
      });
      // Seed pending for the newly loaded page (#2069).
      this.rootStore.requestWorkflow.seedPendingForSubmissions(page.results);
    } catch (err) {
      if (seq !== this.submissionsSeq) {
        console.warn("[intake] superseded submissions load-more failed", { slug }, err);
        return;
      }
      console.warn("[intake] load more submissions failed", { slug }, err);
      runInAction(() => {
        this.submissionsError = (err as Error).message;
      });
    } finally {
      // Cleared even when superseded: the flag gates starting a load-more, so
      // no other one can have started while this one held it.
      runInAction(() => {
        this.isLoadingMoreSubmissions = false;
      });
    }
  }

  // --- Board view: per-status (per-column) pagination ---------------------

  /** Read a column's pagination slice, or an empty default if not yet fetched. */
  getBoardColumn(stateId: string): TBoardColumnState {
    return this.boardColumns.get(stateId) ?? emptyBoardColumn();
  }

  /** Fetch page 1 for each given status column, replacing any prior slices.
   *  Call on board mount and whenever the shared filters/search/archive change. */
  fetchBoardColumns(slug: string, stateIds: string[]): void {
    // Drop columns no longer present (e.g. a status was deleted) so stale
    // slices don't linger in the map.
    runInAction(() => {
      const keep = new Set(stateIds);
      for (const id of [...this.boardColumns.keys()]) {
        if (!keep.has(id)) this.boardColumns.delete(id);
      }
    });
    for (const stateId of stateIds) {
      void this.fetchBoardColumn(slug, stateId);
    }
  }

  /** Replace a single column with its first page (per-column page size). */
  async fetchBoardColumn(slug: string, stateId: string): Promise<void> {
    const seq = (this.boardColumnSeq.get(stateId) ?? 0) + 1;
    this.boardColumnSeq.set(stateId, seq);
    const isCurrent = () => this.boardColumnSeq.get(stateId) === seq;
    runInAction(() => {
      // ``isLoadingMore`` is released here because any load-more in flight is
      // now superseded and will write nothing, including its own flag clear.
      const next = {
        ...this.getBoardColumn(stateId),
        isLoading: true,
        isLoadingMore: false,
        error: null,
      };
      this.boardColumns.set(stateId, next);
    });
    try {
      const page = await this.rootStore.intakeService.listSubmissions(
        slug,
        this.leanListQuery(null, {
          status: [stateId],
          per_page: BOARD_COLUMN_PAGE_SIZE,
        }),
      );
      // Superseded by a newer fetch of this column: an older filter set's
      // cards, which must not overwrite the newer ones (NG-1724).
      if (!isCurrent()) return;
      runInAction(() => {
        this.boardColumns.set(stateId, {
          submissions: page.results,
          cursor: page.next_page_results ? page.next_cursor : null,
          hasMore: page.next_page_results,
          total: page.total_count,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        });
      });
      // Seed pending so the board's status cells don't fetch per card (#2069).
      this.rootStore.requestWorkflow.seedPendingForSubmissions(page.results);
    } catch (err) {
      if (!isCurrent()) {
        console.warn("[intake] superseded board column load failed", { slug, stateId }, err);
        return;
      }
      console.warn("[intake] board column load failed", { slug, stateId }, err);
      runInAction(() => {
        this.boardColumns.set(stateId, {
          ...this.getBoardColumn(stateId),
          isLoading: false,
          error: (err as Error).message,
        });
      });
    }
  }

  /** Append the next page to a column. No-op when no cursor or already loading
   *  (the board wires this to an intersection observer that can fire repeatedly
   *  while the sentinel is on-screen). */
  async fetchMoreBoardColumn(slug: string, stateId: string): Promise<void> {
    const column = this.getBoardColumn(stateId);
    if (!column.hasMore || !column.cursor || column.isLoadingMore) return;
    // A page-1 refetch in flight is about to replace this column, so its
    // cursor walks cards on their way out (NG-1724).
    if (column.isLoading) return;
    const seq = this.boardColumnSeq.get(stateId) ?? 0;
    const isCurrent = () => (this.boardColumnSeq.get(stateId) ?? 0) === seq;
    runInAction(() => {
      this.boardColumns.set(stateId, { ...this.getBoardColumn(stateId), isLoadingMore: true });
    });
    try {
      const page = await this.rootStore.intakeService.listSubmissions(
        slug,
        this.leanListQuery(column.cursor, {
          status: [stateId],
          per_page: BOARD_COLUMN_PAGE_SIZE,
        }),
      );
      // The column was refetched while this page was in flight: it is the next
      // page of the OLD slice, so appending it would mix filter sets. The
      // refetch already released ``isLoadingMore``, and a newer load-more may
      // own it by now, so this writes nothing at all.
      if (!isCurrent()) return;
      runInAction(() => {
        const current = this.getBoardColumn(stateId);
        // Guard against duplicate ids if a row was patched into the slice
        // between fetches (e.g. an optimistic state move appending a row).
        const known = new Set(current.submissions.map((s) => s.id));
        const fresh = page.results.filter((s) => !known.has(s.id));
        this.boardColumns.set(stateId, {
          ...current,
          submissions: [...current.submissions, ...fresh],
          cursor: page.next_page_results ? page.next_cursor : null,
          hasMore: page.next_page_results,
          total: page.total_count,
          isLoadingMore: false,
        });
      });
      // Seed pending for the newly loaded board page (#2069).
      this.rootStore.requestWorkflow.seedPendingForSubmissions(page.results);
    } catch (err) {
      if (!isCurrent()) {
        console.warn("[intake] superseded board column load-more failed", { slug, stateId }, err);
        return;
      }
      console.warn("[intake] board column load-more failed", { slug, stateId }, err);
      runInAction(() => {
        this.boardColumns.set(stateId, {
          ...this.getBoardColumn(stateId),
          isLoadingMore: false,
          error: (err as Error).message,
        });
      });
    }
  }

  async fetchSubmission(
    slug: string,
    submissionId: string,
    view?: TRequestViewSource,
  ): Promise<TIntakeSubmission | null> {
    try {
      const submission = await this.rootStore.intakeService.getSubmission(slug, submissionId);
      if (view) track("request_viewed", { request_id: submission.id, ...view });
      runInAction(() => {
        this.submissions = this.submissions.some((s) => s.id === submission.id)
          ? this.submissions.map((s) => (s.id === submission.id ? submission : s))
          : [submission, ...this.submissions];
        this.activeSubmissionId = submission.id;
      });
      // The detail payload folds in pending too; seed it so the detail header's
      // status select reads pending without a separate request (#2069).
      this.rootStore.requestWorkflow.seedPendingForSubmissions([submission]);
      return submission;
    } catch (err) {
      runInAction(() => {
        this.submissionsError = (err as Error).message;
      });
      return null;
    }
  }

  /**
   * Fetch the submission's render definition from the pinned-snapshot /api/v2/
   * route (#3160) and cache it under ``submissionDefinitions``. The detail page
   * renders THIS definition rather than ``form_detail.definition`` — the latter
   * flows through the legacy blob route, which serves an empty definition when
   * the snapshot fails the strict validity gate (duplicate option UUIDs, #3417)
   * and crashed the FE. Returns the definition, or null on failure (the caller
   * falls back to ``form_detail.definition``, keeping the #3633 null-guard).
   */
  async fetchSubmissionDefinition(
    slug: string,
    submissionId: string,
  ): Promise<TIntakeDefinition | null> {
    try {
      const { definition } = await this.rootStore.intakeService.getSubmissionDefinition(
        slug,
        submissionId,
      );
      runInAction(() => {
        this.submissionDefinitions.set(submissionId, definition);
      });
      return definition;
    } catch (err) {
      // Don't swallow: log so a failed definition fetch is visible. The detail
      // page falls back to form_detail.definition, so this degrades rather than
      // breaks the read.
      console.error("[intake] failed to fetch submission definition", {
        slug,
        submissionId,
        error: err,
      });
      return null;
    }
  }

  /**
   * Soft-delete a submission via the BE. Removes the row from the cached
   * ``submissions`` list on success so the requests-list page reflects the
   * removal without a fetch round-trip. Errors propagate to the caller so
   * the UI can show a toast and keep the user on the detail page.
   */
  async deleteSubmission(slug: string, submissionId: string, source = "page"): Promise<void> {
    await this.removeSubmission(slug, submissionId);
    track("request_deleted", { request_id: submissionId, source });
  }

  /**
   * Delete several requests, one call each, as a single user action: one
   * ``request_deleted`` event carrying how many went. Stops at the first
   * failure and rethrows it, after counting the ones already deleted.
   */
  async deleteSubmissions(slug: string, submissionIds: string[], source = "bulk"): Promise<void> {
    let deleted = 0;
    try {
      for (const id of submissionIds) {
        await this.removeSubmission(slug, id);
        deleted += 1;
      }
    } finally {
      if (deleted > 0) track("request_deleted", { count: deleted, source });
    }
  }

  private async removeSubmission(slug: string, submissionId: string): Promise<void> {
    await this.rootStore.intakeService.deleteSubmission(slug, submissionId);
    runInAction(() => {
      this.submissions = this.submissions.filter((s) => s.id !== submissionId);
      // Drop the row from any board column slice holding it, decrementing total.
      for (const [stateId, column] of this.boardColumns) {
        if (!column.submissions.some((s) => s.id === submissionId)) continue;
        this.boardColumns.set(stateId, {
          ...column,
          submissions: column.submissions.filter((s) => s.id !== submissionId),
          total: Math.max(0, column.total - 1),
        });
      }
      if (this.activeSubmissionId === submissionId) {
        this.activeSubmissionId = null;
      }
    });
  }

  /** Switches the list's archive partition and refetches page 1. */
  setSubmissionsArchivedView(view: TIntakeSubmissionsArchivedView, slug: string): void {
    if (this.submissionsArchivedView === view) return;
    this.submissionsArchivedView = view;
    void this.fetchSubmissions(slug);
  }

  /**
   * Archive or unarchive a request and patch the result into the caches.
   *
   * Mirrors the server's updated submission into both the detail cache and the
   * list. When the row no longer belongs to the active list view (just
   * archived while viewing "active", or just unarchived while viewing
   * "archived") it's dropped from ``submissions`` so the list stays consistent
   * without a refetch.
   */
  async setSubmissionArchived(
    slug: string,
    submissionId: string,
    archived: boolean,
    source = "page",
  ): Promise<void> {
    const updated = await this.writingSubmission(submissionId, () =>
      this.rootStore.intakeService.setSubmissionArchived(slug, submissionId, archived),
    );
    track(archived ? "request_archived" : "request_restored", { request_id: submissionId, source });
    runInAction(() => {
      // Update the row in place. This is a details-page action and the details
      // page reads its submission from ``submissions.find(id)``, so the row must
      // stay in the cache (with the new ``archived_at``) for the page to reflect
      // the change and the menu to flip to Unarchive. The list re-queries the
      // server (``?archived=`` partition) on its own fetch, so a row that no
      // longer matches the active view drops out on the next list load rather
      // than being mutated out from under the open details page.
      this.submissions = this.submissions.map((s) => (s.id === submissionId ? updated : s));
    });
  }

  /**
   * Optimistically merge ``partial`` into the cached submission's
   * ``response_data``, fire the PATCH, and revert on failure.
   *
   * The store-level snapshot guards against a fast typist firing a second
   * patch before the first resolves: each call snapshots the pre-call
   * ``response_data`` so a failed PATCH only reverts the keys it touched,
   * not any work the user committed in the interim. The latest server-side
   * snapshot wins on success (the BE may have mirrored typed columns or
   * sanitised the payload).
   */
  async patchSubmissionResponseData(
    slug: string,
    submissionId: string,
    partial: Record<string, unknown>,
    source = "page",
  ): Promise<void> {
    const current = this.submissions.find((s) => s.id === submissionId);
    if (!current) return;
    const prevByKey: Record<string, unknown> = {};
    for (const key of Object.keys(partial)) prevByKey[key] = current.response_data[key];

    await this.writingSubmission(submissionId, async () => {
      runInAction(() => {
        this.submissions = this.submissions.map((s) =>
          s.id === submissionId ? { ...s, response_data: { ...s.response_data, ...partial } } : s,
        );
      });
      try {
        const updated = await this.rootStore.intakeService.patchSubmissionResponseData(
          slug,
          submissionId,
          partial,
        );
        runInAction(() => {
          this.submissions = this.submissions.map((s) => (s.id === submissionId ? updated : s));
        });
      } catch (err) {
        runInAction(() => {
          this.submissions = this.submissions.map((s) =>
            s.id === submissionId
              ? { ...s, response_data: { ...s.response_data, ...prevByKey } }
              : s,
          );
        });
        throw err;
      }
    });
    // Keys are form element names (generated ids, or a system field's kind such
    // as ``request_name``), never labels or values.
    track("request_updated", { request_id: submissionId, fields: Object.keys(partial), source });
  }

  /** Move a submission between board column slices to mirror a state change.
   *  Removes it from every column then inserts the (updated) row at the top of
   *  its destination column, adjusting totals. No-op for columns not yet loaded
   *  on the board. Keeps the per-column infinite-scroll view consistent without
   *  a refetch. */
  /** Run ``write`` as a write to ``submissionId``'s row, so a list page
   *  requested before it settles cannot undo it (NG-2463, see
   *  ``submissionWriteClock``). */
  private async writingSubmission<T>(submissionId: string, write: () => Promise<T>): Promise<T> {
    this.submissionWrittenAt.set(submissionId, ++this.submissionWriteClock);
    this.submissionWritesInFlight.set(
      submissionId,
      (this.submissionWritesInFlight.get(submissionId) ?? 0) + 1,
    );
    try {
      return await write();
    } finally {
      const left = (this.submissionWritesInFlight.get(submissionId) ?? 1) - 1;
      if (left > 0) this.submissionWritesInFlight.set(submissionId, left);
      else this.submissionWritesInFlight.delete(submissionId);
      this.submissionWrittenAt.set(submissionId, ++this.submissionWriteClock);
    }
  }

  /** ``page`` with the cached copy of every row a write touched after the page
   *  was requested (or is still touching): the server may have read those rows
   *  before the write committed. Any other row is the page's. */
  private keepRowsWrittenSince(
    page: TIntakeSubmission[],
    requestedAt: number,
  ): TIntakeSubmission[] {
    return page.map((row) => {
      const written =
        this.submissionWritesInFlight.has(row.id) ||
        (this.submissionWrittenAt.get(row.id) ?? 0) > requestedAt;
      return (written && this.submissions.find((s) => s.id === row.id)) || row;
    });
  }

  /** ``submissions`` with ``submissionId``'s row put back to ``row``, leaving
   *  every other row as it is now: a failed write undoes only itself, not a
   *  list page that landed while it was in flight (NG-2463). */
  private restoreSubmissionRow(submissionId: string, row: TIntakeSubmission | undefined): void {
    if (!row) return;
    this.submissions = this.submissions.map((s) => (s.id === submissionId ? row : s));
  }

  /** Find a submission anywhere in the loaded board column slices. */
  private findBoardColumnSubmission(submissionId: string): TIntakeSubmission | undefined {
    for (const column of this.boardColumns.values()) {
      const found = column.submissions.find((s) => s.id === submissionId);
      if (found) return found;
    }
    return undefined;
  }

  private relocateBoardColumnSubmission(
    submission: TIntakeSubmission,
    destinationStateId: string,
  ): void {
    // Snapshot entries before mutating — writing to the ObservableMap while
    // iterating it can skip keys.
    const entries = [...this.boardColumns.entries()];
    for (const [stateId, column] of entries) {
      const had = column.submissions.some((s) => s.id === submission.id);
      const isDestination = stateId === destinationStateId;
      if (!had && !isDestination) continue;
      const without = column.submissions.filter((s) => s.id !== submission.id);
      const submissions = isDestination ? [submission, ...without] : without;
      // total tracks the server-side count: -1 when the row leaves, +1 when it
      // arrives (only if it wasn't already counted in this column).
      const total = isDestination ? (had ? column.total : column.total + 1) : column.total - 1;
      this.boardColumns.set(stateId, { ...column, submissions, total: Math.max(0, total) });
    }
  }

  async updateSubmissionStatus(
    slug: string,
    submissionId: string,
    stateId: string,
    stateDetail: { id: string; name: string; color: string; group: string },
    reason?: TStatusReasonSelection,
    source = "page",
  ): Promise<void> {
    const before =
      this.submissions.find((s) => s.id === submissionId) ??
      this.findBoardColumnSubmission(submissionId);
    const from = { id: before?.status ?? null, group: before?.status_detail?.group ?? null };
    const previousRow = this.submissions.find((s) => s.id === submissionId);
    const boardSnapshot = new Map(
      [...this.boardColumns].map(([id, col]) => [
        id,
        { ...col, submissions: [...col.submissions] },
      ]),
    );
    await this.writingSubmission(submissionId, async () => {
      runInAction(() => {
        this.submissions = this.submissions.map((s) =>
          s.id === submissionId ? { ...s, status: stateId, status_detail: stateDetail } : s,
        );
        // Find the moved row for the board relocate. Prefer the flat list, but
        // fall back to the board column slices — a board card isn't guaranteed to
        // be in ``this.submissions`` (the board paginates per-column, independent
        // of the flat list), and without this fallback the optimistic move would
        // silently no-op and the card would only appear in its new column after a
        // refresh.
        const moved =
          this.submissions.find((s) => s.id === submissionId) ??
          this.findBoardColumnSubmission(submissionId);
        if (moved) {
          this.relocateBoardColumnSubmission(
            { ...moved, status: stateId, status_detail: stateDetail },
            stateId,
          );
        }
      });
      try {
        const updated = await this.rootStore.intakeService.updateSubmissionStatus(
          slug,
          submissionId,
          stateId,
          reason,
        );
        runInAction(() => {
          this.submissions = this.submissions.map((s) => (s.id === submissionId ? updated : s));
          // Replace the optimistic row with the server copy in its column.
          const col = this.boardColumns.get(stateId);
          if (col) {
            this.boardColumns.set(stateId, {
              ...col,
              submissions: col.submissions.map((s) => (s.id === submissionId ? updated : s)),
            });
          }
        });
      } catch (err) {
        runInAction(() => {
          this.restoreSubmissionRow(submissionId, previousRow);
          this.boardColumns = boardSnapshot;
        });
        throw err;
      }
    });
    // Status ids are the workspace's own; the groups are the fixed enum they
    // roll up to, which is what compares across workspaces.
    track("request_status_changed", {
      request_id: submissionId,
      from: from.id,
      to: stateId,
      from_group: from.group,
      to_group: stateDetail.group,
      has_reason: reason !== undefined,
      source,
    });
  }

  /**
   * Move a submission to ``newStateId`` in the board after the server has
   * already changed its state out-of-band — specifically when an approval vote
   * tips a transition over its threshold and the backend executes it in the same
   * request (see the approve/reject endpoints' ``transition_executed`` flag).
   *
   * Unlike ``updateSubmissionStatus`` this does NOT issue a PATCH (the state has
   * already moved); it fetches the fresh submission to get the correct
   * ``state_detail`` for the card pill, then relocates the card across the board
   * columns. Without this, an approved transition only surfaces after a refresh.
   */
  async relocateSubmissionToState(
    slug: string,
    submissionId: string,
    newStateId: string,
  ): Promise<void> {
    const submission = await this.fetchSubmission(slug, submissionId);
    if (!submission) return;
    runInAction(() => {
      this.relocateBoardColumnSubmission(submission, newStateId);
    });
  }

  /** Assign the request owners on a submission (optimistic, rolls back on error). */
  async updateSubmissionOwners(
    slug: string,
    submissionId: string,
    ownerIds: string[],
    source = "page",
  ): Promise<void> {
    const previous = new Set(
      this.submissions.find((s) => s.id === submissionId)?.default_request_owner_ids ?? [],
    );
    const previousRow = this.submissions.find((s) => s.id === submissionId);
    await this.writingSubmission(submissionId, async () => {
      runInAction(() => {
        this.submissions = this.submissions.map((s) =>
          s.id === submissionId ? { ...s, default_request_owner_ids: ownerIds } : s,
        );
      });
      try {
        const updated = await this.rootStore.intakeService.updateSubmissionOwners(
          slug,
          submissionId,
          ownerIds,
        );
        runInAction(() => {
          this.submissions = this.submissions.map((s) => (s.id === submissionId ? updated : s));
        });
      } catch (err) {
        runInAction(() => {
          this.restoreSubmissionRow(submissionId, previousRow);
        });
        throw err;
      }
    });
    // The picker sends the whole owner list; the events count the difference.
    const { added, removed } = listChanges(previous, ownerIds);
    if (added > 0) track("request_assigned", { request_id: submissionId, count: added, source });
    if (removed > 0) {
      track("request_unassigned", { request_id: submissionId, count: removed, source });
    }
  }

  /**
   * Pin a request's priority tier (optimistic, rolls back on error). The pinned
   * tier survives every future recompute until cleared. Optimistically shows the
   * chosen tier with ``is_overridden: true`` (keeping the last known numeric
   * ``score`` for the sort), then trues up to the server row.
   */
  async overrideSubmissionPriority(
    slug: string,
    submissionId: string,
    tier: TPriorityTier,
    source = "page",
  ): Promise<void> {
    const previousRow = this.submissions.find((s) => s.id === submissionId);
    await this.writingSubmission(submissionId, async () => {
      runInAction(() => {
        this.submissions = this.submissions.map((s) =>
          s.id === submissionId
            ? {
                ...s,
                priority: {
                  tier,
                  // Unknown until the server response trues this up (NG-1861) —
                  // the picked tier's custom name is the workspace's own store
                  // label, not derivable from the prior tier's payload label.
                  // `resolveTierLabel` falls back to `tierLabels`/the built-in
                  // default when this is null, so the optimistic row still
                  // renders a name.
                  tier_label: null,
                  score: s.priority?.score ?? 0,
                  is_overridden: true,
                  is_locked: false,
                },
              }
            : s,
        );
      });
      try {
        const updated = await this.rootStore.intakeService.overrideSubmissionPriority(
          slug,
          submissionId,
          tier,
        );
        runInAction(() => {
          this.submissions = this.submissions.map((s) => (s.id === submissionId ? updated : s));
        });
      } catch (err) {
        runInAction(() => {
          this.restoreSubmissionRow(submissionId, previousRow);
        });
        throw err;
      }
    });
    track("request_updated", {
      request_id: submissionId,
      fields: ["priority"],
      priority_override: "set",
      source,
    });
  }

  /**
   * Clear a request's priority override so it rejoins auto-compute. Unlike the
   * other inline edits this is NOT optimistic: the recomputed tier is unknowable
   * client-side (it depends on the workspace's current scoring config), so we
   * apply the server's freshly-computed row rather than guess and flicker.
   */
  async clearSubmissionPriorityOverride(
    slug: string,
    submissionId: string,
    source = "page",
  ): Promise<void> {
    const updated = await this.writingSubmission(submissionId, () =>
      this.rootStore.intakeService.clearSubmissionPriorityOverride(slug, submissionId),
    );
    runInAction(() => {
      this.submissions = this.submissions.map((s) => (s.id === submissionId ? updated : s));
    });
    track("request_updated", {
      request_id: submissionId,
      fields: ["priority"],
      priority_override: "cleared",
      source,
    });
  }

  get activeSubmission(): TIntakeSubmission | null {
    if (!this.activeSubmissionId) return null;
    return this.submissions.find((s) => s.id === this.activeSubmissionId) ?? null;
  }
}
